import { unwrap } from './client';
import type { ApiResponse, PageResponse, SearchParams } from '@/types/api';
import type {
  Employee360Response,
  HrApprovalRequest,
  HrWorkflowConfig,
  HrManagerTeamSummary,
  HrLifecycleEvent,
  HrOrgStructure,
  HrAttendance,
  HrAttendanceBreak,
  HrAttendanceSegment,
  HrDashboardSummary,
  HrExpenseClaim,
  HrTravelRequest,
  HrJobPosting,
  HrLeaveRequest,
  HrPayrollRun,
  HrPayrollTransaction,
  HrRecruitmentPipeline,
  HrRecruitmentPipelineStage,
  HrCandidateSummary,
  HrTalentPoolCandidate,
  HrRecruitmentDashboard,
  HrManpowerRequest,
  HrJobRequisition,
  HrOnboardingTask,
  HrOfferTemplate,
  HrPreboardingItem,
  HrBgvConfig,
  HrAssessmentConfig,
  HrCandidateAction,
  HrWorkforceAnalytics,
  HrPayrollFormula,
  HrPayrollHandoffResponse,
  HrAttendanceDevice,
  HrFeedback360Summary,
  HrTrainingCourse,
  HrAppraisalCycle,
  HrEmployeeReview,
  HrLoanRequest,
  HrAssetRequest,
  HrShift,
  HrScheduleBreak,
  HrDocumentType,
  HrEmployeeDocumentRecord,
  HrEmploymentType,
  HrLeavePolicy,
  HrLeaveType,
  HrOvertimeType,
  HrShiftType,
  HrExpenseType,
  HrAllowanceAssignment,
  HrAllowanceAssignmentHistory,
  HrAllowanceSummary,
  HrAllowanceType,
  HrAssetType,
  HrEmploymentHistory,
  HrExpectedHours,
  HrLeaveDaysPreview,
  HrCompanyHoliday,
  HrHolidayCalendar,
  HrHolidayType,
  HrResolvedCalendar,
  HrMonthlyAttendanceSummary,
  HrEmployeeWorkforceDashboard,
  HrManagerWorkforceDashboard,
  HrHrWorkforceDashboard,
  HrWorkCalendarOverride,
  HrWeeklyOvertimeSummary,
  HrWorkforceSettings,
  HrMasterCorrection,
  HrMasterVersion,
  HrTimesheetDay,
  HrTimesheetSummary,
  HrCompRestOverview,
  HrEssTimesheet,
  HrAbsenceCategory,
  HrRosterCopyRequest,
  HrRosterCopyPreview,
  HrRosterCopyResult,
  HrShiftSwapRequest,
  HrInterview,
  HrJobInterviewRound,
  HrInterviewEvalCriterion,
  HrInterviewScheduleHistory,
  HrRecruitmentTrackingFilters,
  HrRecruitmentTrackingRow,
  HrInterviewManagementRow,
  HrScreeningQuestion,
  HrRecruitmentNotificationTemplate,
  HrCcn,
  HrCcnClassification,
  HrFranceOnboardingLookups,
  HrFranceOnboardingRequest,
  HrFranceOnboardingResponse,
  HrCandidateHireProfile,
} from '@/types/hr';
import type { EntityOptionResponse } from '@/types/entityOption';
import type { EmployeeResponse } from '@/types/employee';
import api from './client';

export const hrApi = {
  dashboardSummary: (period: import('@/types/dashboardPresentation').DashboardPeriod = 'WEEK') =>
    unwrap(api.get<ApiResponse<HrDashboardSummary>>('/v1/hr/dashboard/summary', { params: { period } })),

  employee360: (employeeId: number) =>
    unwrap(api.get<ApiResponse<Employee360Response>>(`/v1/hr/employees/${employeeId}/360`)),

  franceOnboardingLookups: () =>
    unwrap(api.get<ApiResponse<HrFranceOnboardingLookups>>('/v1/hr/employees/onboard/lookups')),

  getCandidateHireProfile: (candidateId: number) =>
    unwrap(api.get<ApiResponse<HrCandidateHireProfile>>(`/v1/hr/recruitment/candidates/${candidateId}/hire-profile`)),

  saveCandidateHireProfile: (candidateId: number, body: HrFranceOnboardingRequest) =>
    unwrap(api.put<ApiResponse<HrCandidateHireProfile>>(`/v1/hr/recruitment/candidates/${candidateId}/hire-profile`, body)),

  createFranceOnboarding: (body: HrFranceOnboardingRequest) =>
    unwrap(api.post<ApiResponse<HrFranceOnboardingResponse>>('/v1/hr/employees/onboard', body)),

  getFranceOnboarding: (employeeId: number) =>
    unwrap(api.get<ApiResponse<HrFranceOnboardingResponse>>(`/v1/hr/employees/${employeeId}/onboard`)),

  updateFranceOnboarding: (employeeId: number, body: HrFranceOnboardingRequest) =>
    unwrap(api.put<ApiResponse<HrFranceOnboardingResponse>>(`/v1/hr/employees/${employeeId}/onboard`, body)),

  listCcns: () => unwrap(api.get<ApiResponse<HrCcn[]>>('/v1/hr/platform/ccns')),
  createCcn: (body: { idccCode: string; name: string; active?: boolean }) =>
    unwrap(api.post<ApiResponse<HrCcn>>('/v1/hr/platform/ccns', body)),
  updateCcn: (id: number, body: { idccCode: string; name: string; active?: boolean }) =>
    unwrap(api.put<ApiResponse<HrCcn>>(`/v1/hr/platform/ccns/${id}`, body)),
  deleteCcn: (id: number) =>
    unwrap(api.delete<ApiResponse<void>>(`/v1/hr/platform/ccns/${id}`)),

  listCcnClassifications: (ccnId?: number) =>
    unwrap(api.get<ApiResponse<HrCcnClassification[]>>('/v1/hr/platform/ccn-classifications', {
      params: ccnId ? { ccnId } : undefined,
    })),
  createCcnClassification: (body: {
    ccnId: number;
    coefficient: string;
    label: string;
    minimumMonthlySalary?: number | null;
    minimumHourlyRate?: number | null;
    active?: boolean;
  }) =>
    unwrap(api.post<ApiResponse<HrCcnClassification>>('/v1/hr/platform/ccn-classifications', body)),
  updateCcnClassification: (
    id: number,
    body: {
      ccnId: number;
      coefficient: string;
      label: string;
      minimumMonthlySalary?: number | null;
      minimumHourlyRate?: number | null;
      active?: boolean;
    },
  ) => unwrap(api.put<ApiResponse<HrCcnClassification>>(`/v1/hr/platform/ccn-classifications/${id}`, body)),
  deleteCcnClassification: (id: number) =>
    unwrap(api.delete<ApiResponse<void>>(`/v1/hr/platform/ccn-classifications/${id}`)),

  seedSetup: () => unwrap(api.post<ApiResponse<Record<string, number>>>('/v1/hr/setup/seed')),
  getSetup: () => unwrap(api.get<ApiResponse<Record<string, unknown>>>('/v1/hr/setup')),

  upsertEmployeeProfile: (body: {
    employeeId: number;
    departmentId?: number;
    designationId?: number;
    gradeId?: number;
    managerId?: number;
    businessUnitId?: number;
    employmentType?: string;
    expectedHours?: number;
    expectedHoursPeriod?: string;
    employeeExpectedHours?: number;
    employeeExpectedHoursPeriod?: string;
  }) => unwrap(api.post<ApiResponse<unknown>>('/v1/hr/employee/profile', body)),

  markAttendance: (body: {
    employeeId?: number;
    shiftId?: number;
    checkIn?: string;
    checkOut?: string;
    checkInMode?: string;
    checkInLatitude?: number;
    checkInLongitude?: number;
    overtimeType?: string;
  }) => unwrap(api.post<ApiResponse<HrAttendance>>('/v1/hr/attendance', body)),

  listVisibleEmployees: (params?: SearchParams) =>
    unwrap(api.get<ApiResponse<PageResponse<EmployeeResponse>>>('/v1/hr/employees', { params })),

  listVisibleEmployeeOptions: (params?: SearchParams) =>
    unwrap(api.get<ApiResponse<PageResponse<EntityOptionResponse>>>('/v1/hr/employees/options', { params })),

  getVisibleEmployee: (employeeId: number) =>
    unwrap(api.get<ApiResponse<EmployeeResponse>>(`/v1/hr/employees/${employeeId}`)),

  checkOutAttendance: (id: number, body?: {
    checkOut?: string;
    checkInMode?: string;
    checkInLatitude?: number;
    checkInLongitude?: number;
    overtimeType?: string;
  }) => unwrap(api.post<ApiResponse<HrAttendance>>(`/v1/hr/attendance/${id}/check-out`, body ?? {})),

  getAttendance: (id: number) =>
    unwrap(api.get<ApiResponse<HrAttendance>>(`/v1/hr/attendance/${id}`)),

  listAttendanceBreaks: (id: number) =>
    unwrap(api.get<ApiResponse<HrAttendanceBreak[]>>(`/v1/hr/attendance/${id}/breaks`)),

  startAttendanceBreak: (id: number, reason?: string) =>
    unwrap(api.post<ApiResponse<HrAttendanceBreak>>(`/v1/hr/attendance/${id}/breaks/start`, { reason })),

  endAttendanceBreak: (id: number) =>
    unwrap(api.post<ApiResponse<HrAttendanceBreak>>(`/v1/hr/attendance/${id}/breaks/end`)),

  updateAttendance: (
    id: number,
    body: {
      employeeId: number;
      shiftId?: number;
      checkIn?: string;
      checkOut?: string;
      overtimeType?: string;
    },
  ) => unwrap(api.put<ApiResponse<HrAttendance>>(`/v1/hr/attendance/${id}`, body)),

  createLeave: (body: {
    employeeId: number;
    leaveTypeId: number;
    startDate?: string;
    endDate?: string;
    selectedDates?: string[];
    reason?: string;
    durationMode?: string;
    halfDaySession?: string;
    leaveHours?: number;
    startTime?: string;
    endTime?: string;
    shiftId?: number;
    affectedShiftIds?: number[];
    attachmentUrl?: string;
  }) => unwrap(api.post<ApiResponse<HrLeaveRequest>>('/v1/hr/leaves', body)),

  approveLeave: (id: number, approverId: number) =>
    unwrap(api.post<ApiResponse<HrLeaveRequest>>(`/v1/hr/leaves/${id}/approve`, null, { params: { approverId } })),

  rejectLeave: (id: number, approverId: number) =>
    unwrap(api.post<ApiResponse<HrLeaveRequest>>(`/v1/hr/leaves/${id}/reject`, null, { params: { approverId } })),

  updateLeave: (id: number, body: {
    employeeId: number;
    leaveTypeId: number;
    startDate?: string;
    endDate?: string;
    selectedDates?: string[];
    reason?: string;
    durationMode?: string;
    halfDaySession?: string;
    leaveHours?: number;
    attachmentUrl?: string;
  }) => unwrap(api.put<ApiResponse<HrLeaveRequest>>(`/v1/hr/leaves/${id}`, body)),

  getLeave: (id: number) => unwrap(api.get<ApiResponse<HrLeaveRequest>>(`/v1/hr/leaves/${id}`)),

  cancelLeave: (id: number) =>
    unwrap(api.post<ApiResponse<HrLeaveRequest>>(`/v1/hr/leaves/${id}/cancel`)),

  cancelAttendance: (id: number) =>
    unwrap(api.post<ApiResponse<import('@/types/hr').HrAttendance>>(`/v1/hr/attendance/${id}/cancel`)),

  listAttendance: (params?: SearchParams) =>
    unwrap(api.get<ApiResponse<PageResponse<HrAttendance>>>('/v1/hr/attendance', { params })),

  listAttendanceSegments: (attendanceId: number) =>
    unwrap(api.get<ApiResponse<HrAttendanceSegment[]>>(`/v1/hr/attendance/${attendanceId}/segments`)),

  listAttendanceSegmentsForRange: (employeeId: number, from: string, to: string) =>
    unwrap(api.get<ApiResponse<HrAttendanceSegment[]>>('/v1/hr/attendance/segments', {
      params: { employeeId, from, to },
    })),

  listLeaves: (params?: SearchParams) =>
    unwrap(api.get<ApiResponse<PageResponse<HrLeaveRequest>>>('/v1/hr/leaves', { params })),

  getWorkforceSettings: () =>
    unwrap(api.get<ApiResponse<HrWorkforceSettings>>('/v1/hr/platform/workforce/settings')),

  saveWorkforceSettings: (body: Partial<HrWorkforceSettings>) =>
    unwrap(api.put<ApiResponse<HrWorkforceSettings>>('/v1/hr/platform/workforce/settings', body)),

  createPayrollRun: (body: {
    runCode: string;
    periodStart: string;
    periodEnd: string;
    currencyCode?: string;
  }) => unwrap(api.post<ApiResponse<HrPayrollRun>>('/v1/hr/payroll/runs', body)),

  listPayrollRuns: (params?: SearchParams) =>
    unwrap(api.get<ApiResponse<PageResponse<HrPayrollRun>>>('/v1/hr/payroll/runs', { params })),

  approvePayrollRun: (id: number) =>
    unwrap(api.post<ApiResponse<HrPayrollRun>>(`/v1/hr/payroll/runs/${id}/approve`)),

  processPayrollRun: (id: number, totalAmount: number) =>
    unwrap(
      api.post<ApiResponse<HrPayrollRun>>(`/v1/hr/payroll/runs/${id}/process`, null, {
        params: { totalAmount },
      }),
    ),

  listExpenses: (params?: SearchParams) =>
    unwrap(api.get<ApiResponse<PageResponse<HrExpenseClaim>>>('/v1/hr/expenses', { params })),

  listTrainingCourses: () =>
    unwrap(api.get<ApiResponse<HrTrainingCourse[]>>('/v1/hr/training/courses')),

  createTrainingCourse: (body: { title: string; category?: string; provider?: string }) =>
    unwrap(api.post<ApiResponse<HrTrainingCourse>>('/v1/hr/training/courses', body)),

  createExpense: (body: {
    employeeId: number;
    claimDate: string;
    totalAmount: number;
    currencyCode?: string;
    expenseTypeCode: string;
    description: string;
    attachmentUrl?: string;
  }) => unwrap(api.post<ApiResponse<HrExpenseClaim>>('/v1/hr/expenses', body)),

  updateExpense: (
    id: number,
    body: {
      employeeId: number;
      claimDate: string;
      totalAmount: number;
      currencyCode?: string;
      expenseTypeCode: string;
      description: string;
      attachmentUrl?: string;
    },
  ) => unwrap(api.put<ApiResponse<HrExpenseClaim>>(`/v1/hr/expenses/${id}`, body)),

  approveExpense: (id: number, comments?: string) =>
    unwrap(
      api.post<ApiResponse<HrExpenseClaim>>(`/v1/hr/expenses/${id}/approve`, null, {
        params: comments ? { comments } : undefined,
      }),
    ),

  listApprovalRequests: (status?: string, mineOnly?: boolean) =>
    unwrap(
      api.get<ApiResponse<HrApprovalRequest[]>>('/v1/hr/approvals', {
        params: { ...(status ? { status } : {}), ...(mineOnly ? { mineOnly: true } : {}) },
      }),
    ),

  actApproval: (id: number, action: 'APPROVE' | 'REJECT', comments?: string) =>
    unwrap(
      api.post<ApiResponse<HrApprovalRequest>>(`/v1/hr/approvals/${id}/act`, null, {
        params: { action, ...(comments ? { comments } : {}) },
      }),
    ),

  listJobs: () => unwrap(api.get<ApiResponse<HrJobPosting[]>>('/v1/hr/recruitment/jobs')),

  createJobPosting: (body: {
    title: string;
    departmentId?: number;
    description?: string;
    location?: string;
    employmentType?: string;
    positionsCount?: number;
    budgetAmount?: number;
    postingChannels?: string;
    statusCode?: string;
    internalOnly?: boolean;
    applicationFieldsJson?: string;
  }) => unwrap(api.post<ApiResponse<HrJobPosting>>('/v1/hr/recruitment/jobs', body)),

  updateJobPosting: (id: number, body: {
    title: string;
    departmentId?: number;
    description?: string;
    location?: string;
    employmentType?: string;
    positionsCount?: number;
    budgetAmount?: number;
    postingChannels?: string;
    internalOnly?: boolean;
    applicationFieldsJson?: string;
  }) => unwrap(api.put<ApiResponse<HrJobPosting>>(`/v1/hr/recruitment/jobs/${id}`, body)),

  addCandidate: (body: {
    jobPostingId?: number;
    fullName: string;
    email?: string;
    phone?: string;
    source?: string;
    skills?: string;
    expectedSalary?: number;
  }) => unwrap(api.post<ApiResponse<unknown>>('/v1/hr/recruitment/candidates', body)),

  moveCandidateStage: (id: number, stage: string) =>
    unwrap(api.post<ApiResponse<unknown>>(`/v1/hr/recruitment/candidates/${id}/stage`, null, { params: { stage } })),

  rejectCandidate: (
    id: number,
    body: { rejectionCategory?: string; rejectionReason?: string; moveToTalentPool?: boolean },
  ) => unwrap(api.post<ApiResponse<unknown>>(`/v1/hr/recruitment/candidates/${id}/reject`, body)),

  reconsiderCandidate: (
    id: number,
    body?: { reconsiderationNotes?: string; targetStage?: string },
  ) => unwrap(api.post<ApiResponse<unknown>>(`/v1/hr/recruitment/candidates/${id}/reconsider`, body ?? {})),

  recruitmentPipeline: (search?: string) =>
    unwrap(
      api.get<ApiResponse<HrRecruitmentPipeline>>('/v1/hr/recruitment/pipeline/v2', {
        params: search ? { search } : undefined,
      }),
    ),

  recruitmentTracking: (filters: HrRecruitmentTrackingFilters = {}) => {
    const { customFields, ...params } = filters;
    const customParams = Object.fromEntries(
      Object.entries(customFields ?? {})
        .filter(([code, value]) => code.trim() && value.trim())
        .map(([code, value]) => [`customField.${code.trim()}`, value.trim()]),
    );
    return unwrap(
      api.get<ApiResponse<PageResponse<HrRecruitmentTrackingRow>>>('/v1/hr/recruitment/tracking', {
        params: { ...params, ...customParams },
      }),
    );
  },

  searchInterviews: (params?: {
    dateFrom?: string;
    dateTo?: string;
    status?: string;
    mine?: boolean;
    pendingFeedback?: boolean;
    page?: number;
    size?: number;
  }) =>
    unwrap(
      api.get<ApiResponse<PageResponse<HrInterviewManagementRow>>>('/v1/hr/recruitment/interviews', { params }),
    ),

  runLeaveAccrual: () => unwrap(api.post<ApiResponse<{ balancesUpdated: number }>>('/v1/hr/leave/accrual/run')),

  leaveBalances: (employeeId: number) =>
    unwrap(api.get<ApiResponse<unknown[]>>(`/v1/hr/leave/balances/${employeeId}`)),

  leaveBalanceSummary: (employeeId: number) =>
    unwrap(api.get<ApiResponse<import('@/types/hr').HrLeaveBalanceView[]>>(`/v1/hr/leave/balances/${employeeId}/summary`)),

  essLeaveBalanceSummary: () =>
    unwrap(api.get<ApiResponse<import('@/types/hr').HrLeaveBalanceView[]>>('/v1/hr/ess/leave/balances/summary')),

  adjustLeaveBalance: (body: {
    employeeId: number;
    leaveTypeId: number;
    daysDelta: number;
    reason?: string;
    year?: number;
  }) => unwrap(api.post<ApiResponse<unknown>>('/v1/hr/leave/balances/adjust', body)),

  runLeaveYearEnd: (fromYear?: number) =>
    unwrap(
      api.post<ApiResponse<{ recordsProcessed: number }>>('/v1/hr/leave/year-end/run', null, {
        params: fromYear != null ? { fromYear } : undefined,
      }),
    ),

  leaveRequestHistory: (leaveRequestId: number) =>
    unwrap(api.get<ApiResponse<import('@/types/hr').HrLeaveHistoryView>>(`/v1/hr/leave/requests/${leaveRequestId}/history`)),

  calculatePayroll: (id: number, countryCode?: string) =>
    unwrap(
      api.post<ApiResponse<HrPayrollRun>>(`/v1/hr/payroll/runs/${id}/calculate`, null, {
        params: { countryCode },
      }),
    ),

  workforceAnalytics: () =>
    unwrap(api.get<ApiResponse<HrWorkforceAnalytics>>('/v1/hr/analytics/workforce')),

  essProfile: () => unwrap(api.get<ApiResponse<Employee360Response>>('/v1/hr/ess/me')),

  essUpdateBankDetails: (body: {
    bankName?: string;
    bankBranch?: string;
    bankAccountNumber?: string;
    bankIfscCode?: string;
  }) =>
    unwrap(
      api.put<ApiResponse<Employee360Response['bankDetails']>>('/v1/hr/ess/me/bank-details', body),
    ),

  updateEmployeeBankDetails: (
    employeeId: number,
    body: {
      bankName?: string;
      bankBranch?: string;
      bankAccountNumber?: string;
      bankIfscCode?: string;
    },
  ) =>
    unwrap(
      api.put<ApiResponse<Employee360Response['bankDetails']>>(
        `/v1/hr/employees/${employeeId}/bank-details`,
        body,
      ),
    ),
  essWorkspace: (start?: string, end?: string) =>
    unwrap(
      api.get<ApiResponse<import('@/types/hr').HrEssWorkspace>>('/v1/hr/ess/workspace', {
        params: { start, end },
      }),
    ),

  essSubmittedApprovals: () =>
    unwrap(
      api.get<ApiResponse<import('@/types/approvals').ApprovalInstance[]>>('/v1/hr/ess/approvals/submitted'),
    ),

  essLeaveApproval: (leaveRequestId: number) =>
    unwrap(
      api.get<ApiResponse<import('@/types/approvals').ApprovalInstance | null>>(
        `/v1/hr/ess/leave/requests/${leaveRequestId}/approval`,
      ),
    ),

  essLeaveCancelApproval: (leaveRequestId: number) =>
    unwrap(
      api.get<ApiResponse<import('@/types/approvals').ApprovalInstance | null>>(
        `/v1/hr/ess/leave/requests/${leaveRequestId}/cancellation/approval`,
      ),
    ),

  essLeaveApprovalTimeline: (leaveRequestId: number) =>
    unwrap(
      api.get<ApiResponse<import('@/types/approvals').ApprovalActionRecord[]>>(
        `/v1/hr/ess/leave/requests/${leaveRequestId}/approval/timeline`,
      ),
    ),

  essLeaveCancelApprovalTimeline: (leaveRequestId: number) =>
    unwrap(
      api.get<ApiResponse<import('@/types/approvals').ApprovalActionRecord[]>>(
        `/v1/hr/ess/leave/requests/${leaveRequestId}/cancellation/approval/timeline`,
      ),
    ),

  essApplyLeave: (body: {
    leaveTypeId: number;
    startDate?: string;
    endDate?: string;
    selectedDates?: string[];
    reason?: string;
    durationMode?: string;
    halfDaySession?: string;
    leaveHours?: number;
    attachmentUrl?: string;
  }) => unwrap(api.post<ApiResponse<HrLeaveRequest>>('/v1/hr/ess/leave', body)),

  essPreviewLeaveDays: (
    selectedDates: string[],
    opts?: { durationMode?: string; halfDaySession?: string; leaveHours?: number },
  ) => {
    const sorted = [...selectedDates].sort();
    return unwrap(
      api.get<ApiResponse<import('@/types/hr').HrLeaveDaysPreview>>('/v1/hr/ess/leave/days-preview', {
        params: {
          selectedDates: sorted,
          startDate: sorted[0],
          endDate: sorted[sorted.length - 1],
          ...opts,
        },
      }),
    );
  },

  essGetLeave: (id: number) =>
    unwrap(api.get<ApiResponse<HrLeaveRequest>>(`/v1/hr/ess/leave/requests/${id}`)),

  essUpdateLeave: (id: number, body: {
    leaveTypeId: number;
    startDate?: string;
    endDate?: string;
    selectedDates?: string[];
    reason?: string;
    durationMode?: string;
    halfDaySession?: string;
    leaveHours?: number;
    attachmentUrl?: string;
  }) => unwrap(api.put<ApiResponse<HrLeaveRequest>>(`/v1/hr/ess/leave/requests/${id}`, body)),

  essCheckIn: (body: {
    checkIn?: string;
    checkOut?: string;
    checkInMode?: string;
    checkInLatitude?: number;
    checkInLongitude?: number;
    shiftId?: number;
    deviceFingerprint?: string;
    devicePlatform?: string;
  }) => unwrap(api.post<ApiResponse<HrAttendance>>('/v1/hr/ess/attendance/check-in', body)),

  essCheckOut: (body: {
    checkInMode?: string;
    checkInLatitude?: number;
    checkInLongitude?: number;
    checkOut?: string;
    deviceFingerprint?: string;
    devicePlatform?: string;
  }) => unwrap(api.post<ApiResponse<HrAttendance>>('/v1/hr/ess/attendance/check-out', body)),

  essListAttendanceBreaks: (id: number) =>
    unwrap(api.get<ApiResponse<HrAttendanceBreak[]>>(`/v1/hr/ess/attendance/${id}/breaks`)),

  essStartAttendanceBreak: (id: number, reason?: string) =>
    unwrap(api.post<ApiResponse<HrAttendanceBreak>>(`/v1/hr/ess/attendance/${id}/breaks/start`, { reason })),

  essEndAttendanceBreak: (id: number) =>
    unwrap(api.post<ApiResponse<HrAttendanceBreak>>(`/v1/hr/ess/attendance/${id}/breaks/end`)),

  essMobileDeviceStatus: () =>
    unwrap(api.get<ApiResponse<import('@/types/hr').HrEssMobileDeviceStatus>>('/v1/hr/ess/mobile-device')),

  listEmployeeMobileDevices: (employeeId: number) =>
    unwrap(
      api.get<ApiResponse<import('@/types/hr').HrEmployeeMobileDeviceList>>(
        `/v1/hr/employees/${employeeId}/mobile-devices`,
      ),
    ),

  bindEmployeeMobileDevice: (
    employeeId: number,
    body: { deviceFingerprint: string; deviceLabel?: string; platform?: string },
  ) =>
    unwrap(
      api.put<ApiResponse<import('@/types/hr').HrEmployeeMobileDevice>>(
        `/v1/hr/employees/${employeeId}/mobile-device`,
        body,
      ),
    ),

  revokeEmployeeMobileDevice: (employeeId: number, deviceId: number) =>
    unwrap(api.delete<ApiResponse<null>>(`/v1/hr/employees/${employeeId}/mobile-devices/${deviceId}`)),

  listFormulas: (countryCode?: string) =>
    unwrap(api.get<ApiResponse<HrPayrollFormula[]>>('/v1/hr/payroll/formulas', { params: { countryCode } })),

  listDevices: () => unwrap(api.get<ApiResponse<HrAttendanceDevice[]>>('/v1/hr/devices')),

  registerDevice: (body: { deviceName: string; deviceCode: string; deviceType: string; location?: string }) =>
    unwrap(api.post<ApiResponse<HrAttendanceDevice>>('/v1/hr/devices/register', body)),

  launchFeedback360: (cycleId: number, revieweeEmployeeId: number) =>
    unwrap(
      api.post<ApiResponse<unknown>>(`/v1/hr/feedback/cycles/${cycleId}/launch`, null, {
        params: { revieweeEmployeeId },
      }),
    ),

  feedbackSummary: (cycleId: number, revieweeEmployeeId: number) =>
    unwrap(
      api.get<ApiResponse<HrFeedback360Summary>>('/v1/hr/feedback/summary', {
        params: { cycleId, revieweeEmployeeId },
      }),
    ),

  exportReport: async (
    reportType: string,
    format: 'CSV' | 'EXCEL',
    params?: { dateFrom?: string; dateTo?: string; status?: string; employeeId?: number },
  ) => {
    const res = await api.get(`/v1/hr/reports/export`, {
      params: {
        reportType,
        format,
        ...(params?.dateFrom ? { dateFrom: params.dateFrom } : {}),
        ...(params?.dateTo ? { dateTo: params.dateTo } : {}),
        ...(params?.status ? { status: params.status } : {}),
        ...(params?.employeeId ? { employeeId: params.employeeId } : {}),
      },
      responseType: 'blob',
    });
    return res.data as Blob;
  },

  downloadPayslipPdf: (payrollRunId: number, employeeId: number) =>
    api
      .get(`/v1/hr/reports/payslip/${payrollRunId}/${employeeId}`, { responseType: 'blob' })
      .then((r) => r.data as Blob),

  downloadEssPayslipPdf: (payrollRunId: number) =>
    api.get(`/v1/hr/ess/payslips/${payrollRunId}/pdf`, { responseType: 'blob' }).then((r) => r.data as Blob),

  downloadStatutoryIndiaCsv: (payrollRunId: number) =>
    api
      .get(`/v1/hr/reports/statutory/india/${payrollRunId}`, { responseType: 'blob' })
      .then((r) => r.data as Blob),

  submitStatutoryFiling: (payrollRunId: number, portalCode: 'EPFO' | 'ESIC' | 'TRACES') =>
    unwrap(
      api.post<ApiResponse<{ id: number; statusCode: string; referenceNumber?: string }>>(
        `/v1/hr/reports/statutory/india/${payrollRunId}/file/${portalCode}`,
      ),
    ),

  listStatutoryFilings: (payrollRunId: number) =>
    unwrap(
      api.get<ApiResponse<Array<{ id: number; portalCode: string; statusCode: string; referenceNumber?: string }>>>(
        `/v1/hr/reports/statutory/india/${payrollRunId}/filings`,
      ),
    ),

  listAppraisalCycles: () =>
    unwrap(api.get<ApiResponse<HrAppraisalCycle[]>>('/v1/hr/appraisal/cycles')),

  createAppraisalCycle: (body: {
    cycleName: string;
    startDate: string;
    endDate: string;
    statusCode?: string;
  }) => unwrap(api.post<ApiResponse<HrAppraisalCycle>>('/v1/hr/appraisal/cycles', body)),

  listAppraisalReviews: (cycleId: number) =>
    unwrap(api.get<ApiResponse<HrEmployeeReview[]>>(`/v1/hr/appraisal/cycles/${cycleId}/reviews`)),

  submitAppraisalReview: (body: {
    appraisalCycleId: number;
    employeeId: number;
    reviewerId?: number;
    score?: number;
    comments?: string;
    statusCode?: string;
  }) => unwrap(api.post<ApiResponse<HrEmployeeReview>>('/v1/hr/appraisal/reviews', body)),

  approveAppraisalReview: (id: number) =>
    unwrap(api.post<ApiResponse<HrEmployeeReview>>(`/v1/hr/appraisal/reviews/${id}/approve`)),

  listLoans: () => unwrap(api.get<ApiResponse<HrLoanRequest[]>>('/v1/hr/loans')),

  createLoan: (body: {
    employeeId: number;
    loanType?: string;
    principalAmount: number;
    currencyCode?: string;
    tenureMonths?: number;
    reason?: string;
  }) => unwrap(api.post<ApiResponse<HrLoanRequest>>('/v1/hr/loans', body)),

  submitLoan: (id: number) => unwrap(api.post<ApiResponse<HrLoanRequest>>(`/v1/hr/loans/${id}/submit`)),

  approveLoan: (id: number) => unwrap(api.post<ApiResponse<HrLoanRequest>>(`/v1/hr/loans/${id}/approve`)),

  listAssetRequests: () => unwrap(api.get<ApiResponse<HrAssetRequest[]>>('/v1/hr/asset-requests')),

  createAssetRequest: (body: {
    employeeId: number;
    assetType: string;
    quantity?: number;
    reason?: string;
  }) => unwrap(api.post<ApiResponse<HrAssetRequest>>('/v1/hr/asset-requests', body)),

  submitAssetRequest: (id: number) =>
    unwrap(api.post<ApiResponse<HrAssetRequest>>(`/v1/hr/asset-requests/${id}/submit`)),

  approveAssetRequest: (id: number) =>
    unwrap(api.post<ApiResponse<HrAssetRequest>>(`/v1/hr/asset-requests/${id}/approve`)),

  listShifts: () => unwrap(api.get<ApiResponse<HrShift[]>>('/v1/hr/shifts')),

  createShift: (body: {
    name: string;
    shiftType: string;
    startTime?: string;
    endTime?: string;
    breakMinutes?: number;
    breaks?: HrScheduleBreak[];
    workingDaysMask?: number;
    color?: string;
  }) =>
    unwrap(api.post<ApiResponse<HrShift>>('/v1/hr/shifts', body)),

  updateShift: (
    id: number,
    body: {
      name: string;
      shiftType: string;
      startTime?: string;
      endTime?: string;
      breakMinutes?: number;
      breaks?: HrScheduleBreak[];
      workingDaysMask?: number;
      color?: string;
      active?: boolean;
      effectiveFrom?: string;
    },
  ) => unwrap(api.put<ApiResponse<HrShift>>(`/v1/hr/shifts/${id}`, body)),

  deactivateShift: (id: number) =>
    unwrap(api.post<ApiResponse<HrShift>>(`/v1/hr/shifts/${id}/deactivate`)),

  listCorrections: () =>
    unwrap(api.get<ApiResponse<HrMasterCorrection[]>>('/v1/hr/corrections')),

  getCorrection: (id: number) =>
    unwrap(api.get<ApiResponse<HrMasterCorrection>>(`/v1/hr/corrections/${id}`)),

  submitCorrection: (body: {
    correctionType: string;
    targetMasterType: string;
    targetMasterId: number;
    effectiveFrom: string;
    effectiveTo?: string | null;
    affectedEmployeeIds?: number[];
    payload?: Record<string, unknown>;
    reason: string;
    employeeId?: number;
  }) => unwrap(api.post<ApiResponse<HrMasterCorrection>>('/v1/hr/corrections', body)),

  cancelCorrection: (id: number, reason?: string) =>
    unwrap(api.post<ApiResponse<HrMasterCorrection>>(`/v1/hr/corrections/${id}/cancel`, { reason })),

  getCorrectionAudit: (id: number) =>
    unwrap(api.get<ApiResponse<Record<string, unknown>>>(`/v1/hr/corrections/${id}/audit`)),

  listMasterVersions: (masterType: string, masterId: number) =>
    unwrap(api.get<ApiResponse<HrMasterVersion[]>>('/v1/hr/master-versions', {
      params: { masterType, masterId },
    })),

  assignRoster: (body: {
    employeeId: number;
    shiftId: number;
    rosterDate: string;
    departmentId?: number;
  }) => unwrap(api.post<ApiResponse<unknown>>('/v1/hr/roster', body)),

  replaceRosterDay: (body: {
    employeeId: number;
    rosterDate: string;
    shiftId?: number | null;
    departmentId?: number;
  }) => unwrap(api.put<ApiResponse<unknown>>('/v1/hr/roster/day', body)),

  listRoster: (from?: string, to?: string) =>
    unwrap(api.get<ApiResponse<unknown[]>>('/v1/hr/roster', { params: { from, to } })),

  uploadDocument: (
    file: File,
    employeeId: number,
    documentTypeId: number,
    details?: {
      documentName?: string;
      documentNumber?: string;
      issueDate?: string;
      expiryDate?: string;
    },
  ) => {
    const form = new FormData();
    form.append('file', file);
    form.append('employeeId', String(employeeId));
    form.append('documentTypeId', String(documentTypeId));
    if (details?.documentName) form.append('documentName', details.documentName);
    if (details?.documentNumber) form.append('documentNumber', details.documentNumber);
    if (details?.issueDate) form.append('issueDate', details.issueDate);
    if (details?.expiryDate) form.append('expiryDate', details.expiryDate);
    return unwrap(
      api.post<ApiResponse<HrEmployeeDocumentRecord>>('/v1/hr/documents/upload', form, {
        transformRequest: [(data, headers) => {
          if (data instanceof FormData) {
            delete headers['Content-Type'];
          }
          return data;
        }],
      }),
    );
  },

  essUploadDocument: (
    file: File,
    documentTypeId: number,
    details?: {
      documentName?: string;
      documentNumber?: string;
      issueDate?: string;
      expiryDate?: string;
    },
  ) => {
    const form = new FormData();
    form.append('file', file);
    form.append('documentTypeId', String(documentTypeId));
    if (details?.documentName) form.append('documentName', details.documentName);
    if (details?.documentNumber) form.append('documentNumber', details.documentNumber);
    if (details?.issueDate) form.append('issueDate', details.issueDate);
    if (details?.expiryDate) form.append('expiryDate', details.expiryDate);
    return unwrap(
      api.post<ApiResponse<HrEmployeeDocumentRecord>>('/v1/hr/ess/documents/upload', form, {
        transformRequest: [(data, headers) => {
          if (data instanceof FormData) {
            delete headers['Content-Type'];
          }
          return data;
        }],
      }),
    );
  },

  listWorkflows: () => unwrap(api.get<ApiResponse<HrWorkflowConfig[]>>('/v1/hr/workflows')),

  saveWorkflow: (body: {
    workflowName: string;
    workflowType: string;
    active?: boolean;
    stages: HrWorkflowConfig['stages'];
  }) => unwrap(api.put<ApiResponse<HrWorkflowConfig>>('/v1/hr/workflows', body)),

  getOrgStructure: () => unwrap(api.get<ApiResponse<HrOrgStructure>>('/v1/hr/org/structure')),

  createDepartment: (body: { name: string; code: string; parentDepartmentId?: number; businessUnitId?: number }) =>
    unwrap(api.post<ApiResponse<unknown>>('/v1/hr/org/departments', body)),

  createDesignation: (body: {
    name: string;
    code: string;
    parentDepartmentId?: number;
    gradeId?: number;
    grade?: string;
  }) => unwrap(api.post<ApiResponse<unknown>>('/v1/hr/org/designations', body)),

  createGrade: (body: {
    name: string;
    code: string;
    levelOrder?: number;
    description?: string;
  }) => unwrap(api.post<ApiResponse<unknown>>('/v1/hr/org/grades', body)),

  createBusinessUnit: (body: { name: string; code: string }) =>
    unwrap(api.post<ApiResponse<unknown>>('/v1/hr/org/business-units', body)),

  updateBusinessUnit: (id: number, body: { name: string; code: string; active?: boolean }) =>
    unwrap(api.put<ApiResponse<unknown>>(`/v1/hr/org/business-units/${id}`, body)),

  deactivateBusinessUnit: (id: number) =>
    unwrap(api.delete<ApiResponse<null>>(`/v1/hr/org/business-units/${id}`)),

  updateDepartment: (
    id: number,
    body: { name: string; code: string; parentDepartmentId?: number; businessUnitId?: number; active?: boolean },
  ) => unwrap(api.put<ApiResponse<unknown>>(`/v1/hr/org/departments/${id}`, body)),

  deactivateDepartment: (id: number) =>
    unwrap(api.delete<ApiResponse<null>>(`/v1/hr/org/departments/${id}`)),

  updateGrade: (
    id: number,
    body: { name: string; code: string; levelOrder?: number; description?: string; active?: boolean },
  ) => unwrap(api.put<ApiResponse<unknown>>(`/v1/hr/org/grades/${id}`, body)),

  deactivateGrade: (id: number) =>
    unwrap(api.delete<ApiResponse<null>>(`/v1/hr/org/grades/${id}`)),

  updateDesignation: (
    id: number,
    body: {
      name: string;
      code: string;
      parentDepartmentId?: number;
      gradeId?: number;
      grade?: string;
      active?: boolean;
    },
  ) => unwrap(api.put<ApiResponse<unknown>>(`/v1/hr/org/designations/${id}`, body)),

  deactivateDesignation: (id: number) =>
    unwrap(api.delete<ApiResponse<null>>(`/v1/hr/org/designations/${id}`)),

  assignManager: (employeeId: number, managerId: number, levelOrder?: number) =>
    unwrap(
      api.post<ApiResponse<unknown>>('/v1/hr/org/hierarchy', null, {
        params: { employeeId, managerId, levelOrder },
      }),
    ),

  removeManager: (employeeId: number) =>
    unwrap(
      api.delete<ApiResponse<null>>('/v1/hr/org/hierarchy', {
        params: { employeeId },
      }),
    ),

  hierarchyTree: (params?: { rootEmployeeId?: number; scope?: 'COMPANY' | 'MY_TEAM' }) =>
    unwrap(api.get<ApiResponse<import('@/types/hr').HrHierarchyTreeResponse>>('/v1/hr/org/hierarchy-tree', { params })),

  reportingManagerPreview: (employeeId: number) =>
    unwrap(api.get<ApiResponse<import('@/types/hr').HrReportingManagerPreview | null>>(
      `/v1/hr/org/reporting-manager/${employeeId}`,
    )),

  mssTeam: (managerEmployeeId?: number) =>
    unwrap(
      api.get<ApiResponse<unknown[]>>('/v1/hr/mss/team', {
        params: managerEmployeeId ? { managerEmployeeId } : undefined,
      }),
    ),

  mssSummary: (managerEmployeeId?: number) =>
    unwrap(
      api.get<ApiResponse<HrManagerTeamSummary>>('/v1/hr/mss/summary', {
        params: managerEmployeeId ? { managerEmployeeId } : undefined,
      }),
    ),

  mssEmployeeProfile: (employeeId: number) =>
    unwrap(api.get<ApiResponse<Employee360Response>>(`/v1/hr/mss/employees/${employeeId}`)),

  mssEmployeeWorkspace: (employeeId: number, start?: string, end?: string) =>
    unwrap(
      api.get<ApiResponse<import('@/types/hr').HrEssWorkspace>>(
        `/v1/hr/mss/employees/${employeeId}/workspace`,
        { params: { start, end } },
      ),
    ),

  mssEmployeeLeaveBalances: (employeeId: number) =>
    unwrap(
      api.get<ApiResponse<import('@/types/hr').HrLeaveBalanceView[]>>(
        `/v1/hr/mss/employees/${employeeId}/leave-balances`,
      ),
    ),

  lifecycleHistory: (employeeId: number) =>
    unwrap(api.get<ApiResponse<HrLifecycleEvent[]>>(`/v1/hr/lifecycle/${employeeId}/history`)),

  recordLifecycleChange: (body: {
    employeeId: number;
    eventType: string;
    effectiveDate: string;
    metadataJson?: string;
  }) => unwrap(api.post<ApiResponse<HrLifecycleEvent>>('/v1/hr/lifecycle/change', body)),

  requestAttendanceAdjustment: (body: {
    employeeId: number;
    attendanceId?: number;
    requestedCheckIn?: string;
    requestedCheckOut?: string;
    reason?: string;
  }) => unwrap(api.post<ApiResponse<unknown>>('/v1/hr/attendance/adjustments', body)),

  approveAttendance: (id: number, approverId: number) =>
    unwrap(
      api.post<ApiResponse<HrAttendance>>(`/v1/hr/attendance/${id}/approve`, null, {
        params: { approverId },
      }),
    ),

  completeHire: (id: number) =>
    unwrap(api.post<ApiResponse<Record<string, unknown>>>(`/v1/hr/recruitment/candidates/${id}/complete-hire`)),

  scheduleInterview: (
    candidateId: number,
    body: {
      scheduledAt: string;
      endAt?: string;
      timeZone?: string;
      interviewerEmployeeId?: number;
      interviewerEmployeeIds?: number[];
      interviewType?: string;
      roundName?: string;
      roundConfigId?: number;
      locationOrLink?: string;
      instructions?: string;
      evaluationFormJson?: string;
      durationMinutes?: number;
    },
  ) => unwrap(api.post<ApiResponse<HrInterview>>(`/v1/hr/recruitment/candidates/${candidateId}/interviews`, body)),

  completeInterview: (interviewId: number, body: {
    score?: number;
    feedback?: string;
    recommendation?: 'STRONG_HIRE' | 'HIRE' | 'HOLD' | 'NO_HIRE';
    strengths?: string;
    weaknesses?: string;
    decisionCode?: string;
    ratings?: Record<string, number>;
  }) =>
    unwrap(api.post<ApiResponse<HrInterview>>(`/v1/hr/recruitment/interviews/${interviewId}/complete`, body)),

  listCandidateInterviews: (candidateId: number) =>
    unwrap(api.get<ApiResponse<HrInterview[]>>(`/v1/hr/recruitment/candidates/${candidateId}/interviews`)),

  getInterview: (interviewId: number) =>
    unwrap(api.get<ApiResponse<HrInterview>>(`/v1/hr/recruitment/interviews/${interviewId}`)),

  interviewHistory: (interviewId: number) =>
    unwrap(api.get<ApiResponse<HrInterviewScheduleHistory[]>>(
      `/v1/hr/recruitment/interviews/${interviewId}/history`,
    )),

  myInterviews: (pendingOnly = false) =>
    unwrap(api.get<ApiResponse<HrInterview[]>>('/v1/hr/recruitment/interviews/mine', { params: { pendingOnly } })),

  submitInterviewFeedback: (interviewId: number, body: {
    score?: number;
    feedback?: string;
    recommendation?: 'STRONG_HIRE' | 'HIRE' | 'HOLD' | 'NO_HIRE';
    strengths?: string;
    weaknesses?: string;
    ratings?: Record<string, number>;
  }) => unwrap(api.post<ApiResponse<HrInterview>>(`/v1/hr/recruitment/interviews/${interviewId}/feedback`, body)),

  rescheduleInterview: (interviewId: number, body: {
    scheduledAt: string;
    endAt?: string;
    durationMinutes?: number;
    timeZone?: string;
    locationOrLink?: string;
    instructions?: string;
    reason?: string;
  }) => unwrap(api.post<ApiResponse<HrInterview>>(`/v1/hr/recruitment/interviews/${interviewId}/reschedule`, body)),

  cancelInterview: (interviewId: number, reason: string) =>
    unwrap(api.post<ApiResponse<HrInterview>>(`/v1/hr/recruitment/interviews/${interviewId}/cancel`, { reason })),

  updateInterviewStatus: (interviewId: number, status: 'CONFIRMED' | 'NO_SHOW') =>
    unwrap(api.post<ApiResponse<HrInterview>>(`/v1/hr/recruitment/interviews/${interviewId}/status`, { status })),

  listInterviewRounds: (jobId: number) =>
    unwrap(api.get<ApiResponse<HrJobInterviewRound[]>>(`/v1/hr/recruitment/jobs/${jobId}/interview-rounds`)),

  createInterviewRound: (jobId: number, body: Omit<HrJobInterviewRound, 'id' | 'jobPostingId'>) =>
    unwrap(api.post<ApiResponse<HrJobInterviewRound>>(`/v1/hr/recruitment/jobs/${jobId}/interview-rounds`, body)),

  updateInterviewRound: (id: number, body: Omit<HrJobInterviewRound, 'id' | 'jobPostingId'>) =>
    unwrap(api.put<ApiResponse<HrJobInterviewRound>>(`/v1/hr/recruitment/interview-rounds/${id}`, body)),

  deleteInterviewRound: (id: number) =>
    unwrap(api.delete<ApiResponse<void>>(`/v1/hr/recruitment/interview-rounds/${id}`)),

  reorderInterviewRounds: (jobId: number, roundIds: number[]) =>
    unwrap(api.put<ApiResponse<HrJobInterviewRound[]>>(
      `/v1/hr/recruitment/jobs/${jobId}/interview-rounds/reorder`,
      roundIds,
    )),

  createInterviewCriterion: (body: { code: string; label: string; sortOrder?: number; active?: boolean }) =>
    unwrap(api.post<ApiResponse<HrInterviewEvalCriterion>>('/v1/hr/recruitment/interview-criteria', body)),

  updateInterviewCriterion: (id: number, body: { code: string; label: string; sortOrder?: number; active?: boolean }) =>
    unwrap(api.put<ApiResponse<HrInterviewEvalCriterion>>(`/v1/hr/recruitment/interview-criteria/${id}`, body)),

  deleteInterviewCriterion: (id: number) =>
    unwrap(api.delete<ApiResponse<void>>(`/v1/hr/recruitment/interview-criteria/${id}`)),

  createOffer: (
    candidateId: number,
    body: {
      salary: number;
      currency?: string;
      joiningDate?: string;
      validUntil?: string;
      content?: string;
      employmentType?: string;
      templateId?: number;
    },
  ) => unwrap(api.post<ApiResponse<{ id: number }>>(`/v1/hr/recruitment/candidates/${candidateId}/offers`, body)),

  offerVersionHistory: (candidateId: number) =>
    unwrap(api.get<ApiResponse<Record<string, unknown>[]>>(
      `/v1/hr/recruitment/candidates/${candidateId}/offers`,
    )),

  listOfferTemplates: (activeOnly = true) =>
    unwrap(api.get<ApiResponse<HrOfferTemplate[]>>('/v1/hr/recruitment/offer-templates', {
      params: { activeOnly },
    })),

  createOfferTemplate: (body: {
    templateName: string;
    templateCode: string;
    contentTemplate: string;
    benefitsJson?: string;
    active?: boolean;
  }) => unwrap(api.post<ApiResponse<HrOfferTemplate>>('/v1/hr/recruitment/offer-templates', body)),

  updateOfferTemplate: (
    id: number,
    body: {
      templateName?: string;
      templateCode?: string;
      contentTemplate?: string;
      benefitsJson?: string;
      active?: boolean;
    },
  ) => unwrap(api.put<ApiResponse<HrOfferTemplate>>(`/v1/hr/recruitment/offer-templates/${id}`, body)),

  deleteOfferTemplate: (id: number) =>
    unwrap(api.delete<ApiResponse<unknown>>(`/v1/hr/recruitment/offer-templates/${id}`)),

  listScreeningQuestions: (jobId: number, activeOnly = false) =>
    unwrap(api.get<ApiResponse<HrScreeningQuestion[]>>(
      `/v1/hr/recruitment/jobs/${jobId}/screening-questions`,
      { params: { activeOnly } },
    )),

  createScreeningQuestion: (
    jobId: number,
    body: Omit<HrScreeningQuestion, 'id' | 'jobPostingId'>,
  ) => unwrap(api.post<ApiResponse<HrScreeningQuestion>>(
    `/v1/hr/recruitment/jobs/${jobId}/screening-questions`,
    body,
  )),

  updateScreeningQuestion: (
    questionId: number,
    body: Omit<HrScreeningQuestion, 'id' | 'jobPostingId'>,
  ) => unwrap(api.put<ApiResponse<HrScreeningQuestion>>(
    `/v1/hr/recruitment/screening-questions/${questionId}`,
    body,
  )),

  deleteScreeningQuestion: (questionId: number) =>
    unwrap(api.delete<ApiResponse<void>>(`/v1/hr/recruitment/screening-questions/${questionId}`)),

  overrideScreeningAnswer: (answerId: number, reason: string) =>
    unwrap(api.post<ApiResponse<unknown>>(
      `/v1/hr/recruitment/screening-answers/${answerId}/override`,
      { reason },
    )),

  listRecruitmentNotificationTemplates: () =>
    unwrap(api.get<ApiResponse<HrRecruitmentNotificationTemplate[]>>(
      '/v1/hr/recruitment/notification-templates',
    )),

  createRecruitmentNotificationTemplate: (
    body: Omit<HrRecruitmentNotificationTemplate, 'id' | 'ownerModule' | 'version' | 'published'> & {
      published?: boolean;
    },
  ) => unwrap(api.post<ApiResponse<HrRecruitmentNotificationTemplate>>(
    '/v1/hr/recruitment/notification-templates',
    body,
  )),

  updateRecruitmentNotificationTemplate: (
    id: number,
    body: Partial<HrRecruitmentNotificationTemplate>,
  ) => unwrap(api.put<ApiResponse<HrRecruitmentNotificationTemplate>>(
    `/v1/hr/recruitment/notification-templates/${id}`,
    body,
  )),

  publishRecruitmentNotificationTemplate: (id: number) =>
    unwrap(api.post<ApiResponse<HrRecruitmentNotificationTemplate>>(
      `/v1/hr/recruitment/notification-templates/${id}/publish`,
    )),

  deleteRecruitmentNotificationTemplate: (id: number) =>
    unwrap(api.delete<ApiResponse<void>>(`/v1/hr/recruitment/notification-templates/${id}`)),

  getRecruitmentStageMetadata: () =>
    unwrap(api.get<ApiResponse<import('@/types/hr').HrStageMetadata[]>>('/v1/hr/recruitment/stages/metadata')),

  getCandidateStageMetadata: (candidateId: number) =>
    unwrap(api.get<ApiResponse<import('@/types/hr').HrStageMetadata[]>>(`/v1/hr/recruitment/stages/metadata/${candidateId}`)),

  listRecruitmentPipelines: () =>
    unwrap(api.get<ApiResponse<import('@/types/hr').HrRecruitmentPipelineConfig[]>>('/v1/hr/recruitment/pipelines')),

  getRecruitmentPipeline: (id: number) =>
    unwrap(api.get<ApiResponse<import('@/types/hr').HrRecruitmentPipelineConfig>>(`/v1/hr/recruitment/pipelines/${id}`)),

  createRecruitmentPipeline: (body: {
    code: string;
    name: string;
    description?: string;
    active?: boolean;
    isDefault?: boolean;
  }) => unwrap(api.post<ApiResponse<import('@/types/hr').HrRecruitmentPipelineConfig>>('/v1/hr/recruitment/pipelines', body)),

  updateRecruitmentPipeline: (
    id: number,
    body: { name?: string; description?: string; active?: boolean; isDefault?: boolean },
  ) => unwrap(api.put<ApiResponse<import('@/types/hr').HrRecruitmentPipelineConfig>>(`/v1/hr/recruitment/pipelines/${id}`, body)),

  setDefaultRecruitmentPipeline: (id: number) =>
    unwrap(api.post<ApiResponse<import('@/types/hr').HrRecruitmentPipelineConfig>>(`/v1/hr/recruitment/pipelines/${id}/set-default`)),

  listRecruitmentPipelineConfigStages: (pipelineId: number) =>
    unwrap(api.get<ApiResponse<import('@/types/hr').HrPipelineStageConfig[]>>(
      `/v1/hr/recruitment/pipelines/${pipelineId}/stages`,
    )),

  addRecruitmentPipelineStage: (
    pipelineId: number,
    body: import('@/types/hr').HrPipelineStageCreateRequest,
  ) => unwrap(api.post<ApiResponse<import('@/types/hr').HrPipelineStageConfig>>(
    `/v1/hr/recruitment/pipelines/${pipelineId}/stages`,
    body,
  )),

  updateRecruitmentPipelineStageColor: (pipelineId: number, stageId: number, color: string) =>
    unwrap(api.put<ApiResponse<import('@/types/hr').HrPipelineStageConfig>>(
      `/v1/hr/recruitment/pipelines/${pipelineId}/stages/${stageId}/color`,
      { color },
    )),

  reorderRecruitmentPipelineStages: (pipelineId: number, stageCodes: string[]) =>
    unwrap(api.put<ApiResponse<import('@/types/hr').HrPipelineStageConfig[]>>(
      `/v1/hr/recruitment/pipelines/${pipelineId}/stages/reorder`,
      { stageCodes },
    )),

  listRecruitmentPipelineTransitions: (pipelineId: number) =>
    unwrap(api.get<ApiResponse<import('@/types/hr').HrPipelineTransition[]>>(
      `/v1/hr/recruitment/pipelines/${pipelineId}/transitions`,
    )),

  saveRecruitmentPipelineTransitions: (
    pipelineId: number,
    transitions: import('@/types/hr').HrPipelineTransition[],
  ) => unwrap(api.put<ApiResponse<import('@/types/hr').HrPipelineTransition[]>>(
    `/v1/hr/recruitment/pipelines/${pipelineId}/transitions`,
    transitions,
  )),

  listFormFields: (entityScope: string, activeOnly = true) =>
    unwrap(api.get<ApiResponse<import('@/types/hr').HrFormFieldDefinition[]>>('/v1/hr/recruitment/form-fields', {
      params: { entityScope, activeOnly },
    })),

  createFormField: (body: Partial<import('@/types/hr').HrFormFieldDefinition> & {
    entityScope: string;
    fieldCode: string;
    fieldLabel: string;
  }) => unwrap(api.post<ApiResponse<import('@/types/hr').HrFormFieldDefinition>>('/v1/hr/recruitment/form-fields', body)),

  updateFormField: (id: number, body: Partial<import('@/types/hr').HrFormFieldDefinition>) =>
    unwrap(api.put<ApiResponse<import('@/types/hr').HrFormFieldDefinition>>(`/v1/hr/recruitment/form-fields/${id}`, body)),

  deleteFormField: (id: number) =>
    unwrap(api.delete<ApiResponse<unknown>>(`/v1/hr/recruitment/form-fields/${id}`)),

  listFormValues: (entityScope: string, entityId: number) =>
    unwrap(api.get<ApiResponse<import('@/types/hr').HrFormFieldValue[]>>('/v1/hr/recruitment/form-values', {
      params: { entityScope, entityId },
    })),

  setFormValues: (entityScope: string, entityId: number, values: Record<string, string>) =>
    unwrap(api.put<ApiResponse<import('@/types/hr').HrFormFieldValue[]>>('/v1/hr/recruitment/form-values', {
      entityScope,
      entityId,
      values,
    })),

  listInterviewCriteria: (activeOnly = true) =>
    unwrap(api.get<ApiResponse<import('@/types/hr').HrInterviewEvalCriterion[]>>(
      '/v1/hr/recruitment/interview-criteria',
      { params: { activeOnly } },
    )),

  candidateTimeline: (id: number, page = 0, size = 50) =>
    unwrap(api.get<ApiResponse<import('@/types/hr').HrCandidateTimelinePage>>(
      `/v1/hr/recruitment/candidates/${id}/timeline`,
      { params: { page, size } },
    )),

  acceptOffer: (offerId: number, offerVersion: number) =>
    unwrap(api.post<ApiResponse<unknown>>(`/v1/hr/recruitment/offers/${offerId}/accept`, {
      offerVersion,
      acknowledged: true,
    })),

  declineOffer: (offerId: number, body: { offerVersion: number; reason: string; comment?: string }) =>
    unwrap(api.post<ApiResponse<unknown>>(`/v1/hr/recruitment/offers/${offerId}/decline`, body)),

  sendOffer: (offerId: number) =>
    unwrap(api.post<ApiResponse<unknown>>(`/v1/hr/recruitment/offers/${offerId}/send`)),

  remindOffer: (offerId: number) =>
    unwrap(api.post<ApiResponse<unknown>>(`/v1/hr/recruitment/offers/${offerId}/remind`)),

  recordSelectionDecision: (
    candidateId: number,
    body: {
      decision: 'SELECTED' | 'REJECTED' | 'HOLD';
      reason: string;
      recruiterRecommendation?: string;
      hiringManagerRecommendation?: string;
    },
  ) => unwrap(api.post<ApiResponse<unknown>>(
    `/v1/hr/recruitment/candidates/${candidateId}/selection-decision`,
    body,
  )),

  withdrawOffer: (offerId: number) =>
    unwrap(api.post<ApiResponse<unknown>>(`/v1/hr/recruitment/offers/${offerId}/withdraw`)),

  reviseOffer: (
    offerId: number,
    body: {
      salary: number;
      currency?: string;
      joiningDate?: string;
      validUntil?: string;
      content?: string;
      employmentType?: string;
      templateId?: number;
      benefitsJson?: string;
    },
  ) => unwrap(api.post<ApiResponse<{ id: number }>>(`/v1/hr/recruitment/offers/${offerId}/revise`, body)),

  downloadOfferPdf: (offerId: number) =>
    api.get(`/v1/hr/recruitment/offers/${offerId}/pdf`, { responseType: 'blob' }).then((r) => r.data as Blob),

  downloadInterviewIcs: (interviewId: number) =>
    api.get(`/v1/hr/recruitment/interviews/${interviewId}/ics`, { responseType: 'blob' }).then((r) => r.data as Blob),

  publishJob: (jobId: number, channels?: string[]) =>
    unwrap(api.post<ApiResponse<Record<string, unknown>>>(`/v1/hr/recruitment/jobs/${jobId}/publish`, { channels })),

  jobPublishStatus: (jobId: number) =>
    unwrap(api.get<ApiResponse<Record<string, unknown>>>(`/v1/hr/recruitment/jobs/${jobId}/publish-status`)),

  initiateRecruitmentBgv: (
    candidateId: number,
    body?: { verificationType?: string; vendor?: string; externalReference?: string; vendorCaseUrl?: string },
  ) => unwrap(api.post<ApiResponse<unknown>>(`/v1/hr/recruitment/candidates/${candidateId}/bgv`, body ?? {})),

  moveToTalentPool: (candidateId: number, body?: { noteText?: string }) =>
    unwrap(api.post<ApiResponse<HrTalentPoolCandidate>>(`/v1/hr/recruitment/candidates/${candidateId}/talent-pool`, body ?? {})),

  listTalentPool: () =>
    unwrap(api.get<ApiResponse<HrTalentPoolCandidate[]>>('/v1/hr/recruitment/talent-pool')),

  reengageFromTalentPool: (
    candidateId: number,
    body?: { jobPostingId?: number; stageCode?: string },
  ) => unwrap(api.post<ApiResponse<HrCandidateSummary>>(`/v1/hr/recruitment/candidates/${candidateId}/reengage`, body ?? {})),

  completeAllPreboarding: (candidateId: number) =>
    unwrap(api.post<ApiResponse<{ completedItems: number }>>(`/v1/hr/recruitment/candidates/${candidateId}/preboarding/complete-all`)),

  completeAllOnboarding: (candidateId: number) =>
    unwrap(api.post<ApiResponse<{ completedTasks: number }>>(`/v1/hr/recruitment/candidates/${candidateId}/onboarding/complete-all`)),

  approveRequisition: (id: number) =>
    unwrap(api.post<ApiResponse<HrJobRequisition>>(`/v1/hr/recruitment/requisitions/${id}/approve`)),

  recruitmentDashboard: () =>
    unwrap(api.get<ApiResponse<HrRecruitmentDashboard>>('/v1/hr/recruitment/dashboard')),

  recruitmentAnalytics: () =>
    unwrap(api.get<ApiResponse<import('@/types/hr').HrRecruitmentAnalytics>>('/v1/hr/recruitment/analytics')),

  checkCandidateDuplicates: (params: {
    email?: string;
    phone?: string;
    nationalId?: string;
    linkedinUrl?: string;
  }) =>
    unwrap(
      api.get<ApiResponse<{ duplicateFound: boolean; matches: { candidateId: number; fullName: string; matchType: string }[] }>>(
        '/v1/hr/recruitment/candidates/duplicate-check',
        { params },
      ),
    ),

  runAutoScreening: (candidateId: number) =>
    unwrap(api.post<ApiResponse<Record<string, unknown>>>(`/v1/hr/recruitment/candidates/${candidateId}/screening/run`)),

  listCandidateAssessments: (candidateId: number) =>
    unwrap(api.get<ApiResponse<import('@/types/hr').HrCandidateAssessment[]>>(`/v1/hr/recruitment/candidates/${candidateId}/assessments`)),

  scheduleAssessment: (
    candidateId: number,
    body: {
      assessmentType: string;
      assessmentName: string;
      externalUrl?: string;
      externalProvider?: string;
    },
  ) =>
    unwrap(api.post<ApiResponse<import('@/types/hr').HrCandidateAssessment>>(`/v1/hr/recruitment/candidates/${candidateId}/assessments`, body)),

  completeAssessment: (assessmentId: number, body: { score: number; feedback?: string; passed?: boolean }) =>
    unwrap(api.post<ApiResponse<import('@/types/hr').HrCandidateAssessment>>(`/v1/hr/recruitment/assessments/${assessmentId}/complete`, body)),

  approveManpowerRequest: (id: number) =>
    unwrap(api.post<ApiResponse<import('@/types/hr').HrManpowerRequest>>(`/v1/hr/recruitment/workforce-planning/${id}/approve`)),

  confirmProbation: (reviewId: number, decision: 'CONFIRMED' | 'EXTENDED' | 'TERMINATED') =>
    unwrap(api.post<ApiResponse<unknown>>(`/v1/hr/recruitment/probation/${reviewId}/confirm`, null, { params: { decision } })),

  submitProbationReview: (reviewId: number, body: { managerRating?: number; hrRating?: number; managerComments?: string; hrComments?: string }) =>
    unwrap(api.post<ApiResponse<unknown>>(`/v1/hr/recruitment/probation/${reviewId}/review`, body)),

  listManpowerRequests: () =>
    unwrap(api.get<ApiResponse<HrManpowerRequest[]>>('/v1/hr/recruitment/workforce-planning')),

  getManpowerRequest: (id: number) =>
    unwrap(api.get<ApiResponse<HrManpowerRequest>>(`/v1/hr/recruitment/workforce-planning/${id}`)),

  createManpowerRequest: (body: Partial<HrManpowerRequest>) =>
    unwrap(api.post<ApiResponse<HrManpowerRequest>>('/v1/hr/recruitment/workforce-planning', body)),

  updateManpowerRequest: (id: number, body: Partial<HrManpowerRequest>) =>
    unwrap(api.put<ApiResponse<HrManpowerRequest>>(`/v1/hr/recruitment/workforce-planning/${id}`, body)),

  submitManpowerRequest: (id: number) =>
    unwrap(api.post<ApiResponse<HrManpowerRequest>>(`/v1/hr/recruitment/workforce-planning/${id}/submit`)),

  cancelManpowerRequest: (id: number) =>
    unwrap(api.post<ApiResponse<HrManpowerRequest>>(`/v1/hr/recruitment/workforce-planning/${id}/cancel`)),

  deleteManpowerRequest: (id: number) =>
    unwrap(api.delete<ApiResponse<null>>(`/v1/hr/recruitment/workforce-planning/${id}`)),

  createRequisitionFromManpower: (id: number) =>
    unwrap(api.post<ApiResponse<HrJobRequisition>>(`/v1/hr/recruitment/workforce-planning/${id}/requisition`)),

  listRequisitions: () =>
    unwrap(api.get<ApiResponse<HrJobRequisition[]>>('/v1/hr/recruitment/requisitions')),

  getRequisition: (id: number) =>
    unwrap(api.get<ApiResponse<HrJobRequisition>>(`/v1/hr/recruitment/requisitions/${id}`)),

  createRequisition: (body: Partial<HrJobRequisition> & { manpowerRequestId?: number }) =>
    unwrap(api.post<ApiResponse<HrJobRequisition>>('/v1/hr/recruitment/requisitions', body)),

  updateRequisition: (id: number, body: Partial<HrJobRequisition>) =>
    unwrap(api.put<ApiResponse<HrJobRequisition>>(`/v1/hr/recruitment/requisitions/${id}`, body)),

  submitRequisition: (id: number) =>
    unwrap(api.post<ApiResponse<HrJobRequisition>>(`/v1/hr/recruitment/requisitions/${id}/submit`)),

  cancelRequisition: (id: number) =>
    unwrap(api.post<ApiResponse<HrJobRequisition>>(`/v1/hr/recruitment/requisitions/${id}/cancel`)),

  deleteRequisition: (id: number) =>
    unwrap(api.delete<ApiResponse<null>>(`/v1/hr/recruitment/requisitions/${id}`)),

  createPostingFromRequisition: (
    id: number,
    body: {
      title?: string;
      description?: string;
      statusCode?: string;
      postingChannels?: string;
      internalOnly?: boolean;
    },
  ) =>
    unwrap(api.post<ApiResponse<HrJobPosting>>(`/v1/hr/recruitment/requisitions/${id}/posting`, body)),

  candidate360: (id: number) =>
    unwrap(api.get<ApiResponse<Record<string, unknown>>>(`/v1/hr/recruitment/candidates/${id}/360`)),

  candidateActions: (id: number) =>
    unwrap(api.get<ApiResponse<HrCandidateAction[]>>(`/v1/hr/recruitment/candidates/${id}/actions`)),

  listOnboardingTasks: (candidateId: number) =>
    unwrap(api.get<ApiResponse<HrOnboardingTask[]>>(`/v1/hr/recruitment/candidates/${candidateId}/onboarding/tasks`)),

  completeOnboardingTask: (taskId: number, notes?: string) =>
    unwrap(
      api.post<ApiResponse<HrOnboardingTask>>(`/v1/hr/recruitment/onboarding/tasks/${taskId}/complete`, null, {
        params: notes ? { notes } : {},
      }),
    ),

  assignOnboardingTask: (taskId: number, employeeId?: number, ownerRole?: string) =>
    unwrap(
      api.post<ApiResponse<HrOnboardingTask>>(`/v1/hr/recruitment/onboarding/tasks/${taskId}/assign`, null, {
        params: { employeeId, ownerRole },
      }),
    ),

  reopenOnboardingTask: (taskId: number) =>
    unwrap(api.post<ApiResponse<HrOnboardingTask>>(`/v1/hr/recruitment/onboarding/tasks/${taskId}/reopen`)),

  approveOnboarding: (candidateId: number) =>
    unwrap(api.post<ApiResponse<unknown>>(`/v1/hr/recruitment/candidates/${candidateId}/onboarding/approve`)),

  listPreboardingItems: (candidateId: number) =>
    unwrap(api.get<ApiResponse<HrPreboardingItem[]>>(`/v1/hr/recruitment/candidates/${candidateId}/preboarding`)),

  submitPreboardingItem: (
    itemId: number,
    body: { fileUrl?: string; fileName?: string; customFields?: Record<string, string> },
  ) => unwrap(
    api.post<ApiResponse<HrPreboardingItem>>(`/v1/hr/recruitment/preboarding/items/${itemId}/submit`, body),
  ),

  uploadPreboardingItem: (itemId: number, file: File) => {
    const body = new FormData();
    body.append('file', file);
    return unwrap(
      api.post<ApiResponse<HrPreboardingItem>>(
        `/v1/hr/recruitment/preboarding/items/${itemId}/upload`,
        body,
        { headers: { 'Content-Type': 'multipart/form-data' } },
      ),
    );
  },

  downloadPreboardingDocument: (itemId: number) =>
    api.get(`/v1/hr/recruitment/preboarding/items/${itemId}/document`, { responseType: 'blob' })
      .then((response) => response.data as Blob),

  deletePreboardingDocument: (itemId: number) =>
    unwrap(api.delete<ApiResponse<HrPreboardingItem>>(`/v1/hr/recruitment/preboarding/items/${itemId}/document`)),

  remindPreboardingItem: (itemId: number) =>
    unwrap(api.post<ApiResponse<null>>(`/v1/hr/recruitment/preboarding/items/${itemId}/remind`)),

  listBgvConfigs: (category?: string, activeOnly?: boolean) =>
    unwrap(
      api.get<ApiResponse<HrBgvConfig[]>>('/v1/hr/recruitment/settings/bgv', {
        params: { category, activeOnly },
      }),
    ),

  createBgvConfig: (data: Partial<HrBgvConfig>) =>
    unwrap(api.post<ApiResponse<HrBgvConfig>>('/v1/hr/recruitment/settings/bgv', data)),

  updateBgvConfig: (id: number, data: Partial<HrBgvConfig>) =>
    unwrap(api.put<ApiResponse<HrBgvConfig>>(`/v1/hr/recruitment/settings/bgv/${id}`, data)),

  deleteBgvConfig: (id: number) =>
    unwrap(api.delete<ApiResponse<null>>(`/v1/hr/recruitment/settings/bgv/${id}`)),

  listAssessmentConfigs: (activeOnly?: boolean) =>
    unwrap(
      api.get<ApiResponse<HrAssessmentConfig[]>>('/v1/hr/recruitment/settings/assessments', {
        params: { activeOnly },
      }),
    ),

  createAssessmentConfig: (data: Partial<HrAssessmentConfig>) =>
    unwrap(api.post<ApiResponse<HrAssessmentConfig>>('/v1/hr/recruitment/settings/assessments', data)),

  updateAssessmentConfig: (id: number, data: Partial<HrAssessmentConfig>) =>
    unwrap(api.put<ApiResponse<HrAssessmentConfig>>(`/v1/hr/recruitment/settings/assessments/${id}`, data)),

  deleteAssessmentConfig: (id: number) =>
    unwrap(api.delete<ApiResponse<null>>(`/v1/hr/recruitment/settings/assessments/${id}`)),


  listPayrollTransactions: (runId: number) =>
    unwrap(api.get<ApiResponse<HrPayrollTransaction[]>>(`/v1/hr/payroll/runs/${runId}/transactions`)),

  updateFormula: (id: number, body: Partial<HrPayrollFormula>) =>
    unwrap(api.post<ApiResponse<HrPayrollFormula>>(`/v1/hr/payroll/formulas/${id}`, body)),

  previewFormula: (id: number, gross = 100000) =>
    unwrap(api.get<ApiResponse<Record<string, number>>>(`/v1/hr/payroll/formulas/${id}/preview`, { params: { gross } })),

  downloadDisbursementCsv: (runId: number) =>
    api.get(`/v1/hr/platform/payroll/runs/${runId}/disbursement`, { responseType: 'blob' }).then((r) => r.data as Blob),

  listLeaveTypes: () => unwrap(api.get<ApiResponse<HrLeaveType[]>>('/v1/hr/platform/leave/types')),
  createLeaveType: (body: {
    name: string;
    code: string;
    paid?: boolean;
    carryForward?: boolean;
    active?: boolean;
    color?: string;
    behavior?: Record<string, unknown>;
    categoryCode?: string;
  }) => unwrap(api.post<ApiResponse<HrLeaveType>>('/v1/hr/platform/leave/types', body)),
  updateLeaveType: (
    id: number,
    body: {
      name: string;
      code: string;
      paid?: boolean;
      carryForward?: boolean;
      active?: boolean;
      color?: string;
      behavior?: Record<string, unknown>;
      categoryCode?: string;
    },
  ) => unwrap(api.put<ApiResponse<HrLeaveType>>(`/v1/hr/platform/leave/types/${id}`, body)),
  deactivateLeaveType: (id: number) =>
    unwrap(api.delete<ApiResponse<null>>(`/v1/hr/platform/leave/types/${id}`)),
  listLeavePolicies: () => unwrap(api.get<ApiResponse<HrLeavePolicy[]>>('/v1/hr/platform/leave/policies')),
  saveLeavePolicy: (body: {
    leaveTypeId: number;
    policyName: string;
    accrualRuleJson?: string;
    eligibilityRuleJson?: string;
    annualEntitlementDays?: number;
    monthlyAccrualDays?: number;
    maxCarryForwardDays?: number;
    encashmentAllowed?: boolean;
    maxConsecutiveDays?: number;
    attachmentRequired?: boolean;
    approvalRequired?: boolean;
    probationBlocked?: boolean;
    allowBackdated?: boolean;
    maxBackdatedDays?: number;
    reserveOnSubmit?: boolean;
    policyActive?: boolean;
  }) => unwrap(api.put<ApiResponse<HrLeavePolicy>>('/v1/hr/platform/leave/policies', body)),

  listEmploymentTypesAdmin: () =>
    unwrap(api.get<ApiResponse<HrEmploymentType[]>>('/v1/hr/platform/employment-types')),
  createEmploymentType: (body: {
    name: string;
    code: string;
    category?: string;
    requiresEndDate?: boolean;
    payrollMode?: string;
    defaultHoursPerDay?: number;
    active?: boolean;
  }) => unwrap(api.post<ApiResponse<HrEmploymentType>>('/v1/hr/platform/employment-types', body)),
  updateEmploymentType: (
    id: number,
    body: {
      name: string;
      code: string;
      category?: string;
      requiresEndDate?: boolean;
      payrollMode?: string;
      defaultHoursPerDay?: number;
      active?: boolean;
    },
  ) => unwrap(api.put<ApiResponse<HrEmploymentType>>(`/v1/hr/platform/employment-types/${id}`, body)),
  deactivateEmploymentType: (id: number) =>
    unwrap(api.delete<ApiResponse<null>>(`/v1/hr/platform/employment-types/${id}`)),

  listDocumentTypes: () => unwrap(api.get<ApiResponse<HrDocumentType[]>>('/v1/hr/platform/document-types')),
  createDocumentType: (body: { name: string; code: string; expiryTracked?: boolean; active?: boolean }) =>
    unwrap(api.post<ApiResponse<HrDocumentType>>('/v1/hr/platform/document-types', body)),
  updateDocumentType: (
    id: number,
    body: { name: string; code: string; expiryTracked?: boolean; active?: boolean },
  ) => unwrap(api.put<ApiResponse<HrDocumentType>>(`/v1/hr/platform/document-types/${id}`, body)),
  deactivateDocumentType: (id: number) =>
    unwrap(api.delete<ApiResponse<null>>(`/v1/hr/platform/document-types/${id}`)),

  listOvertimeTypes: () => unwrap(api.get<ApiResponse<HrOvertimeType[]>>('/v1/hr/platform/overtime-types')),
  createOvertimeType: (body: {
    name: string;
    code: string;
    description?: string;
    applicableDayClass?: string;
    sortOrder?: number;
    active?: boolean;
  }) => unwrap(api.post<ApiResponse<HrOvertimeType>>('/v1/hr/platform/overtime-types', body)),
  updateOvertimeType: (
    id: number,
    body: {
      name: string;
      code: string;
      description?: string;
      applicableDayClass?: string;
      sortOrder?: number;
      active?: boolean;
    },
  ) => unwrap(api.put<ApiResponse<HrOvertimeType>>(`/v1/hr/platform/overtime-types/${id}`, body)),
  deactivateOvertimeType: (id: number) =>
    unwrap(api.delete<ApiResponse<null>>(`/v1/hr/platform/overtime-types/${id}`)),

  listShiftTypes: () => unwrap(api.get<ApiResponse<HrShiftType[]>>('/v1/hr/platform/shift-types')),
  createShiftType: (body: { name: string; code: string; description?: string; sortOrder?: number; active?: boolean }) =>
    unwrap(api.post<ApiResponse<HrShiftType>>('/v1/hr/platform/shift-types', body)),
  updateShiftType: (
    id: number,
    body: { name: string; code: string; description?: string; sortOrder?: number; active?: boolean },
  ) => unwrap(api.put<ApiResponse<HrShiftType>>(`/v1/hr/platform/shift-types/${id}`, body)),
  deactivateShiftType: (id: number) =>
    unwrap(api.delete<ApiResponse<null>>(`/v1/hr/platform/shift-types/${id}`)),

  listExpenseTypes: () => unwrap(api.get<ApiResponse<HrExpenseType[]>>('/v1/hr/platform/expense-types')),
  createExpenseType: (body: {
    name: string;
    code: string;
    description?: string;
    requiresReceipt?: boolean;
    sortOrder?: number;
    active?: boolean;
  }) => unwrap(api.post<ApiResponse<HrExpenseType>>('/v1/hr/platform/expense-types', body)),
  updateExpenseType: (
    id: number,
    body: {
      name: string;
      code: string;
      description?: string;
      requiresReceipt?: boolean;
      sortOrder?: number;
      active?: boolean;
    },
  ) => unwrap(api.put<ApiResponse<HrExpenseType>>(`/v1/hr/platform/expense-types/${id}`, body)),

  allowanceSummary: () => unwrap(api.get<ApiResponse<HrAllowanceSummary>>('/v1/hr/platform/allowance-summary')),
  listAllowanceTypes: () => unwrap(api.get<ApiResponse<HrAllowanceType[]>>('/v1/hr/platform/allowance-types')),
  createAllowanceType: (body: Partial<HrAllowanceType>) =>
    unwrap(api.post<ApiResponse<HrAllowanceType>>('/v1/hr/platform/allowance-types', body)),
  updateAllowanceType: (id: number, body: Partial<HrAllowanceType>) =>
    unwrap(api.put<ApiResponse<HrAllowanceType>>(`/v1/hr/platform/allowance-types/${id}`, body)),
  deactivateAllowanceType: (id: number) =>
    unwrap(api.delete<ApiResponse<null>>(`/v1/hr/platform/allowance-types/${id}`)),
  listAllowanceAssignments: (employeeId?: number) =>
    unwrap(
      api.get<ApiResponse<HrAllowanceAssignment[]>>('/v1/hr/platform/allowance-assignments', {
        params: employeeId ? { employeeId } : undefined,
      }),
    ),
  createAllowanceAssignment: (body: Partial<HrAllowanceAssignment>) =>
    unwrap(api.post<ApiResponse<HrAllowanceAssignment>>('/v1/hr/platform/allowance-assignments', body)),
  updateAllowanceAssignment: (id: number, body: Partial<HrAllowanceAssignment>) =>
    unwrap(api.put<ApiResponse<HrAllowanceAssignment>>(`/v1/hr/platform/allowance-assignments/${id}`, body)),
  deactivateAllowanceAssignment: (id: number) =>
    unwrap(api.delete<ApiResponse<null>>(`/v1/hr/platform/allowance-assignments/${id}`)),
  allowanceAssignmentHistory: (id: number) =>
    unwrap(api.get<ApiResponse<HrAllowanceAssignmentHistory[]>>(`/v1/hr/platform/allowance-assignments/${id}/history`)),
  myAllowances: () => unwrap(api.get<ApiResponse<HrAllowanceAssignment[]>>('/v1/hr/ess/allowances')),

  listRecruitmentPipelineStages: () =>
    unwrap(api.get<ApiResponse<HrRecruitmentPipelineStage[]>>('/v1/hr/platform/recruitment/pipeline-stages')),
  createRecruitmentPipelineStage: (body: {
    name: string;
    code: string;
    color?: string;
    sortOrder?: number;
    active?: boolean;
    stageType?: string;
    mandatory?: boolean;
    skippable?: boolean;
    requiresApproval?: boolean;
    requiresAssessment?: boolean;
    requiresInterview?: boolean;
    slaHours?: number;
    candidateVisible?: boolean;
  }) =>
    unwrap(
      api.post<ApiResponse<HrRecruitmentPipelineStage>>('/v1/hr/platform/recruitment/pipeline-stages', body),
    ),
  updateRecruitmentPipelineStage: (
    id: number,
    body: { color: string },
  ) =>
    unwrap(
      api.put<ApiResponse<HrRecruitmentPipelineStage>>(`/v1/hr/platform/recruitment/pipeline-stages/${id}`, body),
    ),

  deactivateExpenseType: (id: number) =>
    unwrap(api.delete<ApiResponse<null>>(`/v1/hr/platform/expense-types/${id}`)),

  listAssetTypes: () => unwrap(api.get<ApiResponse<HrAssetType[]>>('/v1/hr/platform/asset-types')),
  createAssetType: (body: {
    code: string;
    name: string;
    description?: string;
    requiresReturn?: boolean;
    sortOrder?: number;
    active?: boolean;
  }) => unwrap(api.post<ApiResponse<HrAssetType>>('/v1/hr/platform/asset-types', body)),
  updateAssetType: (
    id: number,
    body: {
      code: string;
      name: string;
      description?: string;
      requiresReturn?: boolean;
      sortOrder?: number;
      active?: boolean;
    },
  ) => unwrap(api.put<ApiResponse<HrAssetType>>(`/v1/hr/platform/asset-types/${id}`, body)),
  deactivateAssetType: (id: number) =>
    unwrap(api.delete<ApiResponse<null>>(`/v1/hr/platform/asset-types/${id}`)),

  requestOvertime: (body: { employeeId: number; overtimeDate: string; minutes: number; overtimeType: string }) =>
    unwrap(api.post<ApiResponse<unknown>>('/v1/hr/overtime', body)),
  getOvertime: (id: number) =>
    unwrap(api.get<ApiResponse<import('@/types/hr').HrOvertimeRequest>>(`/v1/hr/overtime/${id}`)),
  listOvertime: (params?: { startDate?: string; endDate?: string }) =>
    unwrap(
      api.get<ApiResponse<import('@/types/hr').HrOvertimeRequest[]>>('/v1/hr/overtime', {
        params: params?.startDate && params?.endDate
          ? { startDate: params.startDate, endDate: params.endDate }
          : undefined,
      }),
    ),
  approveOvertime: (id: number) =>
    unwrap(api.post<ApiResponse<import('@/types/hr').HrOvertimeRequest>>(`/v1/hr/overtime/${id}/approve`)),
  cancelOvertime: (id: number) =>
    unwrap(api.post<ApiResponse<import('@/types/hr').HrOvertimeRequest>>(`/v1/hr/overtime/${id}/cancel`)),

  startOnboarding: (employeeId: number, managerId?: number) =>
    unwrap(api.post<ApiResponse<Record<string, unknown>>>(`/v1/hr/onboarding/${employeeId}/start`, null, { params: { managerId } })),
  initiateExit: (body: { employeeId: number; exitType?: string; lastWorkingDay?: string; noticePeriodDays?: number }) =>
    unwrap(api.post<ApiResponse<Record<string, unknown>>>('/v1/hr/exit/initiate', body)),
  finalizeExit: (employeeId: number) =>
    unwrap(api.post<ApiResponse<unknown>>(`/v1/hr/exit/${employeeId}/finalize`)),

  listEmployeeDocuments: (employeeId: number) =>
    unwrap(api.get<ApiResponse<unknown[]>>(`/v1/hr/documents/employee/${employeeId}`)),

  listCompensationStructures: () => unwrap(api.get<ApiResponse<unknown[]>>('/v1/hr/compensation/structures')),
  createCompensationStructure: (name: string, currencyCode?: string, countryCode?: string) =>
    unwrap(api.post<ApiResponse<unknown>>('/v1/hr/platform/compensation/structures', null, { params: { name, currencyCode, countryCode } })),
  assignCompensation: (body: { employeeId: number; structureId: number; effectiveFrom: string; grossAmount: number; effectiveTo?: string }) =>
    unwrap(api.post<ApiResponse<unknown>>('/v1/hr/compensation/assignments', body)),
  listCompensationAssignments: (employeeId: number) =>
    unwrap(api.get<ApiResponse<unknown[]>>(`/v1/hr/compensation/assignments/${employeeId}`)),

  assignTraining: (body: { employeeId: number; courseId: number; dueDate?: string }) =>
    unwrap(api.post<ApiResponse<unknown>>('/v1/hr/training/assignments', body)),

  listTravel: () => unwrap(api.get<ApiResponse<HrTravelRequest[]>>('/v1/hr/platform/travel')),
  createTravel: (params: Record<string, string | number | undefined>) =>
    unwrap(api.post<ApiResponse<HrTravelRequest>>('/v1/hr/platform/travel', null, { params })),
  updateTravel: (id: number, body: Record<string, string | number | undefined | null>) =>
    unwrap(api.put<ApiResponse<HrTravelRequest>>(`/v1/hr/platform/travel/${id}`, body)),
  settleTravel: (id: number, amount: number) =>
    unwrap(
      api.post<ApiResponse<HrTravelRequest>>(`/v1/hr/platform/travel/${id}/settle`, null, {
        params: { amount },
      }),
    ),
  approveTravel: (id: number) => unwrap(api.post<ApiResponse<unknown>>(`/v1/hr/platform/travel/${id}/approve`)),

  listSuccessionPlans: () => unwrap(api.get<ApiResponse<unknown[]>>('/v1/hr/platform/succession')),
  createSuccessionPlan: (params: Record<string, string | number | undefined>) =>
    unwrap(api.post<ApiResponse<unknown>>('/v1/hr/platform/succession', null, { params })),

  initiateBgv: (candidateId: number, type?: string, vendor?: string) =>
    unwrap(api.post<ApiResponse<unknown>>(`/v1/hr/platform/recruitment/candidates/${candidateId}/bgv`, null, { params: { type, vendor } })),

  completeBgv: (bgvId: number, status?: string, summary?: string) =>
    unwrap(
      api.post<ApiResponse<unknown>>(`/v1/hr/recruitment/bgv/${bgvId}/complete`, null, {
        params: { status, summary },
      }),
    ),

  restartBgv: (bgvId: number, reason: string) =>
    unwrap(
      api.post<ApiResponse<unknown>>(`/v1/hr/recruitment/bgv/${bgvId}/restart`, null, {
        params: { reason },
      }),
    ),

  listFieldPermissions: () => unwrap(api.get<ApiResponse<unknown[]>>('/v1/hr/platform/admin/field-permissions')),
  listCountryPacks: () => unwrap(api.get<ApiResponse<unknown[]>>('/v1/hr/platform/admin/country-packs')),
  listShiftRotations: () => unwrap(api.get<ApiResponse<unknown[]>>('/v1/hr/platform/admin/shift-rotations')),

  publicCareersJobs: (organizationId: number) =>
    unwrap(api.get<ApiResponse<HrJobPosting[]>>(`/v1/public/careers/${organizationId}/jobs`)).then((rows) =>
      Array.isArray(rows) ? rows : [],
    ),
  publicCareersJob: (organizationId: number, jobId: number) =>
    unwrap(api.get<ApiResponse<HrJobPosting>>(`/v1/public/careers/${organizationId}/jobs/${jobId}`)),
  publicCareersApply: (
    organizationId: number,
    body: {
      jobPostingId: number;
      fullName: string;
      email?: string;
      phone?: string;
      resumeUrl?: string;
      skills?: string;
      experienceYears?: number;
      education?: string;
      expectedSalary?: number;
      linkedinUrl?: string;
      customFields?: Record<string, string>;
      screeningAnswers?: Record<number, string>;
    },
  ) => unwrap(api.post<ApiResponse<unknown>>(`/v1/public/careers/${organizationId}/apply`, body)),

  publicCareersApplicationForm: (organizationId: number, jobId: number) =>
    unwrap(api.get<ApiResponse<import('@/types/hr').HrCareersApplicationForm>>(
      `/v1/public/careers/${organizationId}/jobs/${jobId}/application-form`,
    )),

  publicCareersUploadResume: async (organizationId: number, jobId: number, file: File) => {
    const form = new FormData();
    form.append('file', file);
    return unwrap(
      api.post<ApiResponse<{ fileUrl: string }>>(
        `/v1/public/careers/${organizationId}/resume?jobId=${encodeURIComponent(jobId)}`,
        form,
        {
        headers: { 'Content-Type': 'multipart/form-data' },
        },
      ),
    );
  },

  listEmploymentTypes: () =>
    unwrap(api.get<ApiResponse<HrEmploymentType[]>>('/v1/hr/workforce/employment-types')),

  employmentHistory: (employeeId: number) =>
    unwrap(api.get<ApiResponse<HrEmploymentHistory[]>>(`/v1/hr/workforce/employees/${employeeId}/employment-history`)),

  changeEmploymentType: (body: {
    employeeId: number;
    employmentTypeCode: string;
    effectiveDate: string;
    endDate?: string;
    reason?: string;
  }) => unwrap(api.post<ApiResponse<HrEmploymentHistory>>('/v1/hr/workforce/employment-type/change', body)),

  expectedHours: (employeeId: number, periodStart: string, periodEnd: string) =>
    unwrap(
      api.get<ApiResponse<HrExpectedHours>>(`/v1/hr/workforce/employees/${employeeId}/expected-hours`, {
        params: { periodStart, periodEnd },
      }),
    ),

  previewLeaveDays: (
    employeeId: number,
    selectedDates: string[],
    opts?: { durationMode?: string; halfDaySession?: string; leaveHours?: number },
  ) => {
    const sorted = [...selectedDates].sort();
    return unwrap(
      api.get<ApiResponse<HrLeaveDaysPreview>>(`/v1/hr/workforce/employees/${employeeId}/leave-days-preview`, {
        params: {
          selectedDates: sorted,
          startDate: sorted[0],
          endDate: sorted[sorted.length - 1],
          ...opts,
        },
      }),
    );
  },

  listCompanyHolidays: (start: string, end: string) =>
    unwrap(
      api.get<ApiResponse<HrCompanyHoliday[]>>('/v1/hr/workforce/holidays', { params: { start, end } }),
    ),

  getHolidayCalendar: () =>
    unwrap(api.get<ApiResponse<HrHolidayCalendar>>('/v1/hr/workforce/holiday-calendar')),

  createHoliday: (body: {
    holidayDate: string;
    holidayName: string;
    holidayType?: string;
    premiumRate?: number | null;
    treatmentCode?: string | null;
  }) => unwrap(api.post<ApiResponse<HrCompanyHoliday>>('/v1/hr/workforce/holidays', body)),

  updateHoliday: (
    id: number,
    body: {
      holidayDate: string;
      holidayName: string;
      holidayType?: string;
      premiumRate?: number | null;
      treatmentCode?: string | null;
    },
  ) => unwrap(api.put<ApiResponse<HrCompanyHoliday>>(`/v1/hr/workforce/holidays/${id}`, body)),

  deleteHoliday: (id: number) =>
    unwrap(api.delete<ApiResponse<null>>(`/v1/hr/workforce/holidays/${id}`)),

  listHolidayTypes: (includeInactive = true) =>
    unwrap(
      api.get<ApiResponse<HrHolidayType[]>>('/v1/hr/workforce/holiday-types', {
        params: { includeInactive },
      }),
    ),

  createHolidayType: (body: {
    code: string;
    name: string;
    description?: string;
    nonWorkingDay?: boolean;
    sortOrder?: number;
    active?: boolean;
  }) => unwrap(api.post<ApiResponse<HrHolidayType>>('/v1/hr/workforce/holiday-types', body)),

  updateHolidayType: (
    id: number,
    body: {
      code: string;
      name: string;
      description?: string;
      nonWorkingDay?: boolean;
      sortOrder?: number;
      active?: boolean;
    },
  ) => unwrap(api.put<ApiResponse<HrHolidayType>>(`/v1/hr/workforce/holiday-types/${id}`, body)),

  deactivateHolidayType: (id: number) =>
    unwrap(api.delete<ApiResponse<null>>(`/v1/hr/workforce/holiday-types/${id}`)),

  listWeekPatterns: (includeInactive = true) =>
    unwrap(
      api.get<ApiResponse<import('@/types/hr').HrWeekPattern[]>>('/v1/hr/workforce/week-patterns', {
        params: { includeInactive },
      }),
    ),

  createWeekPattern: (body: import('@/types/hr').HrWeekPatternForm) =>
    unwrap(api.post<ApiResponse<import('@/types/hr').HrWeekPattern>>('/v1/hr/workforce/week-patterns', body)),

  updateWeekPattern: (id: number, body: import('@/types/hr').HrWeekPatternForm) =>
    unwrap(api.put<ApiResponse<import('@/types/hr').HrWeekPattern>>(`/v1/hr/workforce/week-patterns/${id}`, body)),

  deactivateWeekPattern: (id: number) =>
    unwrap(api.delete<ApiResponse<null>>(`/v1/hr/workforce/week-patterns/${id}`)),

  resolvedCalendar: (employeeId: number, start: string, end: string) =>
    unwrap(
      api.get<ApiResponse<HrResolvedCalendar>>(`/v1/hr/workforce/employees/${employeeId}/resolved-calendar`, {
        params: { start, end },
      }),
    ),

  resolvedCalendarBatch: (employeeIds: number[], start: string, end: string) =>
    unwrap(
      api.get<ApiResponse<HrResolvedCalendar[]>>('/v1/hr/workforce/resolved-calendar', {
        params: { employeeIds, start, end },
      }),
    ),

  calendarLeaves: (params: SearchParams) =>
    unwrap(
      api.get<ApiResponse<import('@/types/hr').HrLeaveRequest[]>>('/v1/hr/workforce/calendar/leaves', {
        params,
      }),
    ),

  calendarAttendance: (params: SearchParams) =>
    unwrap(
      api.get<ApiResponse<import('@/types/hr').HrAttendance[]>>('/v1/hr/workforce/calendar/attendance', {
        params,
      }),
    ),

  assignEmployeeShift: (body: {
    employeeId: number;
    shiftId: number;
    selectedDates?: string[];
    effectiveFrom?: string;
    effectiveTo?: string;
    reason?: string;
  }) => unwrap(api.post<ApiResponse<import('@/types/hr').HrScheduleChangeRequest>>('/v1/hr/shifts/assignments', body)),

  cancelShiftChange: (id: number) =>
    unwrap(
      api.post<ApiResponse<import('@/types/hr').HrScheduleChangeRequest>>(
        `/v1/hr/shifts/change-requests/${id}/cancel`,
      ),
    ),

  listShiftChangeRequests: (params?: {
    employeeIds?: number[];
    start?: string;
    end?: string;
    employeeId?: number;
    manageable?: boolean;
  }) =>
    unwrap(
      api.get<ApiResponse<import('@/types/hr').HrScheduleChangeRequest[]>>('/v1/hr/shifts/change-requests', {
        params: params
          ? {
              employeeIds: params.employeeIds,
              start: params.start,
              end: params.end,
              employeeId: params.employeeId,
              manageable: params.manageable,
            }
          : undefined,
      }),
    ),

  listShiftAssignments: (employeeId?: number) =>
    unwrap(
      api.get<ApiResponse<import('@/types/hr').HrEmployeeShiftAssignment[]>>('/v1/hr/shifts/assignments', {
        params: employeeId != null ? { employeeId } : undefined,
      }),
    ),

  assignDayShift: (body: {
    employeeId: number;
    assignmentDate: string;
    shiftId: number;
    sequenceOrder?: number;
  }) => unwrap(api.post<ApiResponse<unknown>>('/v1/hr/shifts/day-assignments', body)),

  assignDayShiftsBulk: (body: {
    employeeId: number;
    assignmentDate: string;
    shiftIds: number[];
  }) => unwrap(api.post<ApiResponse<unknown[]>>('/v1/hr/shifts/day-assignments/bulk', body)),

  removeDayShift: (id: number) =>
    unwrap(api.delete<ApiResponse<null>>(`/v1/hr/shifts/day-assignments/${id}`)),

  listDayShifts: (employeeId: number, from?: string, to?: string) =>
    unwrap(
      api.get<ApiResponse<unknown[]>>('/v1/hr/shifts/day-assignments', {
        params: { employeeId, from, to },
      }),
    ),

  monthlyAttendanceSummary: (employeeId: number, year: number, month: number) =>
    unwrap(
      api.get<ApiResponse<HrMonthlyAttendanceSummary>>(
        `/v1/hr/workforce/employees/${employeeId}/attendance-summary`,
        { params: { year, month } },
      ),
    ),

  runMonthEnd: (year: number, month: number) =>
    unwrap(api.post<ApiResponse<unknown[]>>('/v1/hr/workforce/month-end/run', null, { params: { year, month } })),

  employeeWorkforceDashboard: (employeeId: number) =>
    unwrap(api.get<ApiResponse<HrEmployeeWorkforceDashboard>>(`/v1/hr/workforce/dashboard/employee/${employeeId}`)),

  managerWorkforceDashboard: (managerId: number) =>
    unwrap(api.get<ApiResponse<HrManagerWorkforceDashboard>>(`/v1/hr/workforce/dashboard/manager/${managerId}`)),

  hrWorkforceDashboard: () =>
    unwrap(api.get<ApiResponse<HrHrWorkforceDashboard>>('/v1/hr/workforce/dashboard/hr')),

  listCalendarOverrides: (employeeId: number, start?: string, end?: string) =>
    unwrap(
      api.get<ApiResponse<HrWorkCalendarOverride[]>>(
        `/v1/hr/workforce/employees/${employeeId}/calendar-overrides`,
        { params: { start, end } },
      ),
    ),

  upsertCalendarOverride: (body: {
    employeeId: number;
    calendarDate: string;
    dayType: string;
    startTime?: string;
    endTime?: string;
    isHalfDay?: boolean;
    reason?: string;
  }) => unwrap(api.post<ApiResponse<HrWorkCalendarOverride>>('/v1/hr/workforce/calendar-overrides', body)),

  deleteCalendarOverride: (employeeId: number, date: string) =>
    unwrap(api.delete<ApiResponse<null>>(`/v1/hr/workforce/employees/${employeeId}/calendar-overrides/${date}`)),

  listWeeklyOvertime: (employeeId: number, periodStart?: string, periodEnd?: string) =>
    unwrap(
      api.get<ApiResponse<HrWeeklyOvertimeSummary[]>>(
        `/v1/hr/workforce/employees/${employeeId}/weekly-overtime`,
        { params: { periodStart, periodEnd } },
      ),
    ),

  aggregateWeeklyOvertime: (employeeId: number, weekStart?: string) =>
    unwrap(
      api.post<ApiResponse<HrWeeklyOvertimeSummary>>('/v1/hr/workforce/weekly-overtime/aggregate', null, {
        params: { employeeId, weekStart },
      }),
    ),

  aggregateCompanyWeeklyOvertime: (weekStart?: string) =>
    unwrap(
      api.post<ApiResponse<{ employeesProcessed: number }>>(
        '/v1/hr/workforce/weekly-overtime/aggregate-company',
        null,
        { params: { weekStart } },
      ),
    ),

  confirmDueProbations: () =>
    unwrap(api.post<ApiResponse<{ confirmed: number }>>('/v1/hr/workforce/probation/confirm-due')),

  downloadHrImportTemplate: (
    type: import('@/modules/hr/components/hrCsvImportTypes').HrCsvImportKind,
    lang?: string,
  ) =>
    api
      .get(`/v1/hr/import/${type}/template`, {
        params: lang ? { lang } : undefined,
        responseType: 'blob',
      })
      .then((r) => r.data as Blob),

  validateHrImport: (
    type: import('@/modules/hr/components/hrCsvImportTypes').HrCsvImportKind,
    file: File,
  ) => {
    const form = new FormData();
    form.append('file', file);
    return unwrap(
      api.post<ApiResponse<import('@/modules/hr/components/hrCsvImportTypes').HrCsvImportResponse>>(
        `/v1/hr/import/${type}/validate`,
        form,
        { headers: { 'Content-Type': 'multipart/form-data' } },
      ),
    );
  },

  importHrCsv: (
    type: import('@/modules/hr/components/hrCsvImportTypes').HrCsvImportKind,
    file: File,
    skipErrors = false,
  ) => {
    const form = new FormData();
    form.append('file', file);
    return unwrap(
      api.post<ApiResponse<import('@/modules/hr/components/hrCsvImportTypes').HrCsvImportResponse>>(
        `/v1/hr/import/${type}/import`,
        form,
        {
          params: { skipErrors },
          headers: { 'Content-Type': 'multipart/form-data' },
        },
      ),
    );
  },

  listTimesheetDays: (employeeId: number, from: string, to: string) =>
    unwrap(
      api.get<ApiResponse<HrTimesheetDay[]>>('/v1/hr/timesheet', {
        params: { employeeId, from, to },
      }),
    ),

  timesheetSummary: (employeeId: number, year: number, month: number) =>
    unwrap(
      api.get<ApiResponse<HrTimesheetSummary>>('/v1/hr/timesheet/summary', {
        params: { employeeId, year, month },
      }),
    ),

  compRestOverview: (employeeId: number, year: number) =>
    unwrap(
      api.get<ApiResponse<HrCompRestOverview>>('/v1/hr/timesheet/comp-rest', {
        params: { employeeId, year },
      }),
    ),

  essTimesheet: (year?: number, month?: number) =>
    unwrap(
      api.get<ApiResponse<HrEssTimesheet>>('/v1/hr/ess/timesheet', {
        params: {
          ...(year != null ? { year } : {}),
          ...(month != null ? { month } : {}),
        },
      }),
    ),

  listAbsenceCategories: (includeInactive = false) =>
    unwrap(
      api.get<ApiResponse<HrAbsenceCategory[]>>('/v1/hr/platform/absence-categories', {
        params: { includeInactive },
      }),
    ),

  createAbsenceCategory: (body: {
    code: string;
    name: string;
    displayOrder?: number;
    active?: boolean;
  }) => unwrap(api.post<ApiResponse<HrAbsenceCategory>>('/v1/hr/platform/absence-categories', body)),

  updateAbsenceCategory: (
    id: number,
    body: { code: string; name: string; displayOrder?: number; active?: boolean },
  ) => unwrap(api.put<ApiResponse<HrAbsenceCategory>>(`/v1/hr/platform/absence-categories/${id}`, body)),

  deactivateAbsenceCategory: (id: number) =>
    unwrap(api.delete<ApiResponse<null>>(`/v1/hr/platform/absence-categories/${id}`)),

  previewRosterCopy: (body: HrRosterCopyRequest) =>
    unwrap(api.post<ApiResponse<HrRosterCopyPreview>>('/v1/hr/roster/copy/preview', body)),

  copyRoster: (body: HrRosterCopyRequest) =>
    unwrap(api.post<ApiResponse<HrRosterCopyResult>>('/v1/hr/roster/copy', body)),

  publishRoster: (weekStart: string, copyRequest?: HrRosterCopyRequest) =>
    unwrap(
      api.post<ApiResponse<unknown>>('/v1/hr/roster/publish', copyRequest ?? null, {
        params: { weekStart },
      }),
    ),

  listEssShiftSwaps: () =>
    unwrap(api.get<ApiResponse<HrShiftSwapRequest[]>>('/v1/hr/ess/shift-swaps')),

  listEssIncomingShiftSwaps: () =>
    unwrap(api.get<ApiResponse<HrShiftSwapRequest[]>>('/v1/hr/ess/shift-swaps/incoming')),

  createEssShiftSwap: (body: {
    targetEmployeeId: number;
    requesterDate: string;
    targetDate?: string;
    requesterShiftId?: number;
    targetShiftId?: number;
    swapType?: 'SWAP' | 'COVER';
    reason?: string;
  }) => unwrap(api.post<ApiResponse<HrShiftSwapRequest>>('/v1/hr/ess/shift-swaps', body)),

  respondEssShiftSwap: (id: number, accept: boolean) =>
    unwrap(
      api.post<ApiResponse<HrShiftSwapRequest>>(`/v1/hr/ess/shift-swaps/${id}/respond`, null, {
        params: { accept },
      }),
    ),

  cancelEssShiftSwap: (id: number) =>
    unwrap(api.post<ApiResponse<HrShiftSwapRequest>>(`/v1/hr/ess/shift-swaps/${id}/cancel`)),

  payrollHandoffPreview: (year: number, month: number, employeeId?: number) =>
    unwrap(
      api.get<ApiResponse<HrPayrollHandoffResponse>>('/v1/hr/reports/payroll-handoff/preview', {
        params: { year, month, ...(employeeId ? { employeeId } : {}) },
      }),
    ),

  downloadPayrollHandoff: async (
    year: number,
    month: number,
    format: 'csv' | 'CSV' | 'EXCEL' | 'xlsx' | 'XLSX' = 'csv',
    employeeId?: number,
  ) => {
    const res = await api.get('/v1/hr/reports/payroll-handoff', {
      params: { year, month, format, ...(employeeId ? { employeeId } : {}) },
      responseType: 'blob',
    });
    return res.data as Blob;
  },
};
